import { measurementInput } from '../../lib/measurementView'
import { MeasurementProvider } from '../../context/MeasurementContext'
import { Measure, Dimensions } from '../../components/Measurement'
import { optimizationReadiness, type PreparationIssue } from '../../lib/optimizationReadiness'
import CreateProjectDialog from '../../components/optimization/CreateProjectDialog'
import OptimizationProjectHeader from '../../components/optimization/OptimizationProjectHeader'
import OptimizationEquipmentOverview from '../../components/optimization/OptimizationEquipmentOverview'
import OptimizationRulesPanel from '../../components/optimization/OptimizationRulesPanel'
import { getVehicleDisplayName } from '../../lib/vehicleHelpers'
import { MeasurementInput } from '../../components/MeasurementInput'
import { MeasurementSystemSelect } from '../../components/MeasurementSystemSelect'
import { convertMeasurement, parseMeasurementUnit, resolveMeasurementSystem } from '../../lib/measurement'
import type { MeasurementSystem } from '../../lib/measurementSystem'
import React, { useState, useCallback, useMemo, useEffect, useRef, Fragment } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useUnsavedChangesWarning } from '../../hooks/useUnsavedChangesWarning'
import { DndContext, closestCenter, DragEndEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  ChevronLeftIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ChevronRightIcon,
  XMarkIcon,
  ArchiveBoxIcon,
  TruckIcon,
  AdjustmentsHorizontalIcon,
  PlusIcon,
  TrashIcon,
  RocketLaunchIcon,
  DocumentDuplicateIcon,
  PencilIcon,
  Bars3Icon,
  CubeIcon,
  ExclamationTriangleIcon,
  ArrowUpTrayIcon,
  MagnifyingGlassIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline'
import Dialog from '../../components/ui/Dialog'
import CargoCsvDialog from '../../components/optimization/CargoCsvDialog'
import ProductCatalogDialog from '../../components/optimization/ProductCatalogDialog'
import VehicleSearchDialog from '../../components/optimization/VehicleSearchDialog'
import { optimizationApi } from '../../services/optimizationApi'
import { collectAllVehicles } from '../../lib/vehicleTransfer'
import { uniqueCargoLineIds } from '../../lib/cargoLinePresentation'
import { DEFAULT_OPTIMIZATION_SEARCH_PROFILE, optionalConstraintNumber } from '../../lib/optimizationFormSettings'
import { jobProjectApi } from '../../services/jobProjectApi'
import { useOptimizationRunLive } from '../../hooks/useOptimizationRunLive'
import { VehicleConfigRail } from '../../components/optimization/vehicle-visualizer/VehicleConfigRail'
import { synchronizeVehicleCombination } from '../../lib/vehicleCoupling'
import { resolveVehicleAxleLimitEnforcement } from '../../lib/vehicleAxlePolicy'
import ProductForm from '../../components/ProductForm'
import { Product } from '../../services/product.service'
import { productService } from '../../services/product.service'
import { adrReferenceService } from '../../services/adrReference.service'
import { Vehicle as VehicleSettings, vehicleService } from '../../services/vehicle.service'
import { settingsService, OptimizationSettings } from '../../services/settings.service'
import {
  OptimizationInput,
  OptimizationSearchProfile,
  LoadSpace3D,
  Item3D,
  Vehicle,
} from '../../types/optimization'
import {
  Layers,
  Thermometer,
  Rotate3D,
  LayerArrowUp,
  LayerArrowDown,
  Table,
  Weight,
  SquareDimensions,
  Group
} from 'lucide-react'
import { opt } from '../../lib/optimizationStyles'
import {
  ItemAdvancedFormValues,
  validateItemAdvancedFields,
  formatItemAdvancedTags,
} from '../../lib/itemFormHelpers'
import { v4 as uuidv4 } from 'uuid'

import { useAuth } from '../../hooks/useAuth'
import { useAccess } from '../../hooks/useAccess'
import { createCargoItemFromPreset } from '../../lib/cargoPreset'
import { createCargoItemsFromCatalog, type CatalogProductSelection } from '../../lib/cargoCatalog'

// ─── Types ────────────────────────────────────────────────────────────────────

type MainTab = 'cargaison' | 'equipement' | 'regles'

const findInvalidPreparationInput = () => document.querySelector<HTMLInputElement>('[data-measurement-input]:invalid:not(:disabled), #panel-regles input:invalid:not(:disabled)')

// ─── Helpers ──────────────────────────────────────────────────────────────────

const isSemiTrailerVehicle = (vehicle?: Vehicle | null): boolean => {
  // Un conteneur ne peut jamais devenir une semi-remorque parce qu'une ancienne
  // configuration d'essieux serait encore présente dans la session.
  if (vehicle?.type === 'CONTAINER' || !!(vehicle as any)?.container_type) return false
  const axleConfig = vehicle?.axle_configuration
  const physicsType = typeof axleConfig === 'object' ? axleConfig?.physicsType : null
  return vehicle?.type === 'SEMI_TRAILER' || physicsType === 'tractor_semitrailer'
}

/**
 * Un tracteur peut être stocké historiquement sous le type générique TRUCK.
 * La sélection s'appuie donc sur son rôle (sous-type ou sellette), jamais sur
 * le seul type technique du véhicule.
 */
const isTractorCandidate = (vehicle?: VehicleSettings | Vehicle | null): boolean => {
  if (!vehicle) return false
  const raw = vehicle as any
  const type = String(raw.type || '').toUpperCase()
  const subtype = String(raw.vehicle_subtype || raw.vehicleSubtype || '').toLowerCase()
  const axleConfig = typeof raw.axle_configuration === 'object' ? raw.axle_configuration : null

  if (type === 'CONTAINER' || type === 'SEMI_TRAILER' || type === 'TRAILER') return false
  return type === 'TRACTOR'
    || raw.is_tractor === true
    || raw.isTractor === true
    || subtype.includes('tracteur')
    || subtype.includes('tractor')
    || Number.isFinite(Number(raw.fifthWheelPosition))
    || Number.isFinite(Number(raw.fifth_wheel?.position))
    || Number.isFinite(Number(axleConfig?.fifthWheelPosition))
}

const PREMIUM_COLORS = [
  '#2563eb', // Blue
  '#059669', // Emerald
  '#7c3aed', // Violet
  '#d97706', // Amber
  '#db2777', // Pink
  '#0891b2', // Cyan
  '#ea580c', // Orange
  '#0d9488', // Teal
  '#4f46e5', // Indigo
  '#9333ea', // Purple
]

// Generate consistent color from string (group name)
const getColorFromGroupName = (groupName: string): string => {
  if (!groupName || groupName === '__ungrouped__') return '#9ca3af' // Gray for ungrouped

  let hash = 0
  for (let i = 0; i < groupName.length; i++) {
    const char = groupName.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash // Convert to 32bit integer
  }

  const index = Math.abs(hash) % PREMIUM_COLORS.length
  return PREMIUM_COLORS[index]
}

const itemToAdvancedFormValues = (item: Item3D): ItemAdvancedFormValues => ({
  dangerous_goods: !!(item.dangerous_goods || item.is_hazardous),
  adr_class: item.adr_class != null ? String(item.adr_class) : '',
  un_number: item.un_number || '',
  unloading_order: item.unloading_order != null ? String(item.unloading_order) : '',
  must_be_on_floor: !!item.must_be_on_floor,
  keep_together_group: item.keep_together_group || '',
  requires_pallet: !!item.requires_pallet,
  pallet_type: item.pallet_type || 'EUR',
  max_pallet_height_mm: item.max_pallet_height_mm != null ? String(item.max_pallet_height_mm) : '',
  pallet_custom_length: item.pallet_custom_length != null ? String(item.pallet_custom_length) : '',
  pallet_custom_width: item.pallet_custom_width != null ? String(item.pallet_custom_width) : '',
  max_items_per_pallet: item.max_items_per_pallet != null ? String(item.max_items_per_pallet) : '',
  pallet_board_height_mm: item.pallet_board_height_mm != null ? String(item.pallet_board_height_mm) : '',
  pallet_empty_weight_kg: item.pallet_empty_weight_kg != null ? String(item.pallet_empty_weight_kg) : '',
  pallet_color: item.pallet_color || '',
  max_pallet_weight_kg: item.max_pallet_weight_kg != null ? String(item.max_pallet_weight_kg) : '',
})

const createNewItem = (index: number): Item3D => ({
  id: uuidv4(),
  description: '',
  length: 1200,
  width: 800,
  height: 1000,
  weight: 15,
  quantity: 1,
  stackable: true,
  fragile: false,
  rotatable: true,
  rotationOptions: {
    allowRotationX: false,
    allowRotationY: false,
    allowRotationZ: true,
    rotationStrategy: 'DEFAULT_FLAT_ROTATIONS',
  },
  color: PREMIUM_COLORS[index % PREMIUM_COLORS.length],
  _dndId: uuidv4(),
  pallet_board_height_mm: undefined,
  pallet_empty_weight_kg: undefined,
  pallet_color: undefined,
  max_pallet_weight_kg: undefined,
})

const defaultFormData = (tenantId?: string | null, jobId?: string): Partial<OptimizationInput> => ({
  tenantId: tenantId || null,
  jobId: jobId || undefined,
  loadSpace: { length: 10000, width: 2000, height: 2000, is_hazardous_compatible: false } as LoadSpace3D,
  items: [],
  constraints: { maxWeight: 25000, maxStackHeight: 2000 },
  options: { prioritizationStrategy: 'VOLUME_DESC', searchProfile: DEFAULT_OPTIMIZATION_SEARCH_PROFILE },
  vehicle: { type: 'OTHER' },
})

const STATUS_LABELS: Record<string, string> = {
  draft: 'Brouillon',
  queued: 'En attente',
  running: 'En cours',
  completed: 'Complété',
  failed: 'Échoué',
  cancelled: 'Annulé',
  cancellation_requested: 'Annulation en cours',
}

const SEARCH_PROFILE_LABELS: Record<OptimizationSearchProfile, string> = {
  fast: 'Rapide',
  balanced: 'Équilibré',
  thorough: 'Approfondi',
}

// ─── Component ────────────────────────────────────────────────────────────────

interface SortableCargoRowProps {
  item: Item3D & { _dndId?: string }
  index: number
  selectedItemIndexes: number[]
  setSelectedItemIndexes: React.Dispatch<React.SetStateAction<number[]>>
  handleUpdateItemField: (index: number, field: any, value: any) => void
  handleDuplicateItem: (index: number) => void
  handleRemoveItem: (index: number) => void
  handleOpenProductForm: (index: number) => void
  availableGroups?: string[]
}

function SortableCargoRow({
  item,
  index,
  selectedItemIndexes,
  setSelectedItemIndexes,
  handleUpdateItemField,
  handleDuplicateItem,
  handleRemoveItem,
  handleOpenProductForm,
}: SortableCargoRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item._dndId || item.id || `fallback-${index}`
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : 1,
    position: isDragging ? 'relative' as const : undefined,
  }

  const itemTags = formatItemAdvancedTags(item)
  const isSelected = selectedItemIndexes.includes(index)
  const quantity = typeof item.quantity === 'number' && Number.isFinite(item.quantity) && item.quantity > 0
    ? item.quantity
    : 1
  const dimensions = [item.length, item.width, item.height]
  const unitVolumeM3 = dimensions.every(value => typeof value === 'number' && Number.isFinite(value) && value > 0)
    ? dimensions.reduce((volume, value) => volume * convertMeasurement(value, parseMeasurementUnit(item.dimension_unit || 'mm'), 'm'), 1)
    : null

  return (
    <tr
      ref={setNodeRef}
      data-cargo-row={index}
      style={style}
      className={`group transition-all ${isDragging
        ? 'bg-orange-100/90 dark:bg-orange-950/40 shadow-xl z-20'
        : isSelected
          ? 'bg-orange-50/60 dark:bg-orange-950/20'
          : 'hover:bg-slate-50/80 dark:hover:bg-slate-800/40'
        }`}
    >
      {/* Color */}
      <td className="w-2 min-w-[8px] p-0">
        <div className="relative h-full min-h-[52px] w-2">
          <span
            className="absolute inset-y-0 left-0 w-2"
            style={{ backgroundColor: item.color || '#3b82f6' }}
          />
          <input
            type="color"
            value={item.color || '#3b82f6'}
            onChange={(e) => handleUpdateItemField(index, 'color', e.target.value)}
            className="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0"
            title="Couleur de l'article en 3D"
            aria-label="Couleur de l'article en 3D"
            data-no-dnd="true"
          />
        </div>
      </td>

      {/* DRAG HANDLE */}
      <td className="py-2.5 px-1.5 text-center w-6">
        <div>
          <button {...attributes} {...listeners}
            type="button"
            className="cursor-grab active:cursor-grabbing p-1 text-slate-400 hover:text-orange-500 rounded-md transition-colors touch-none"
            aria-label={`Déplacer la ligne ${index + 1}`} title="Glisser ou utiliser Espace puis les flèches pour réorganiser"
          >
            <Bars3Icon className="w-4 h-4 mx-auto" />
          </button>
        </div>
      </td>

      {/* Checkbox */}
      <td className="py-2.5 px-2 text-center w-8">
        <input
          type="checkbox"
          checked={isSelected}
          aria-label={`Sélectionner la ligne ${index + 1}`}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedItemIndexes(prev => [...prev, index])
            } else {
              setSelectedItemIndexes(prev => prev.filter(i => i !== index))
            }
          }}
          className="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-orange-500 focus:ring-orange-500 cursor-pointer"
          data-no-dnd="true"
        />
      </td>

      {/* Ordre # */}
      <td className="py-2.5 px-1.5 w-12 text-center">
        <input
          type="number"
          min={1}
          value={item.unloading_order || ''}
          onChange={(e) => handleUpdateItemField(index, 'unloading_order', e.target.value ? parseInt(e.target.value, 10) : undefined)}
          placeholder="1"
          className="w-full text-xs font-mono text-center py-1.5 px-1 border border-slate-200 dark:border-slate-700/80 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none font-bold"
          data-no-dnd="true"
        />
      </td>

      {/* Description */}
      <td className="py-2.5 px-2 w-[180px] min-w-0">
        <input
          type="text"
          aria-label={`Description de la ligne ${index + 1}`}
          value={item.description || ''}
          onChange={(e) => handleUpdateItemField(index, 'description', e.target.value)}
          placeholder="Libellé / Note"
          className="w-full text-xs py-1.5 px-2 border border-slate-200 dark:border-slate-700/80 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-colors"
        />
        {item.sku && <p title={`SKU : ${item.sku}`} className="mt-1 truncate px-2 text-[11px] text-slate-500 dark:text-slate-400">SKU : {item.sku}</p>}
      </td>

      {/* Length */}
      <td className="py-2.5 px-1 w-20">
        <div className="relative flex items-center">
          <MeasurementInput data-cargo-field="length" compact label="Longueur" quantity="cargoLength" storageUnit={parseMeasurementUnit(item.dimension_unit || 'mm')} value={item.length ?? null} nullable={false} min={Number.MIN_VALUE} onValueChange={v => handleUpdateItemField(index, 'length', v ?? 0)} className="w-full text-xs font-mono border rounded-lg bg-white dark:bg-slate-900 px-1 py-1.5" />
        </div>
      </td>

      {/* Width */}
      <td className="py-2.5 px-1 w-20">
        <div className="relative flex items-center">
          <MeasurementInput data-cargo-field="width" compact label="Largeur" quantity="cargoLength" storageUnit={parseMeasurementUnit(item.dimension_unit || 'mm')} value={item.width ?? null} nullable={false} min={Number.MIN_VALUE} onValueChange={v => handleUpdateItemField(index, 'width', v ?? 0)} className="w-full text-xs font-mono border rounded-lg bg-white dark:bg-slate-900 px-1 py-1.5" />
        </div>
      </td>

      {/* Height */}
      <td className="py-2.5 px-1 w-20">
        <div className="relative flex items-center">
          <MeasurementInput data-cargo-field="height" compact label="Hauteur" quantity="cargoLength" storageUnit={parseMeasurementUnit(item.dimension_unit || 'mm')} value={item.height ?? null} nullable={false} min={Number.MIN_VALUE} onValueChange={v => handleUpdateItemField(index, 'height', v ?? 0)} className="w-full text-xs font-mono border rounded-lg bg-white dark:bg-slate-900 px-1 py-1.5" />
        </div>
      </td>

      {/* Calculated unit volume */}
      <td className="w-24 whitespace-nowrap px-2 py-2.5 text-right text-xs font-mono text-slate-700 dark:text-slate-300"
        title="Volume d’un colis : longueur × largeur × hauteur. Le total de cargaison tient compte des quantités.">
        <Measure value={unitVolumeM3} from="m3" quantity="volume" />
      </td>

      {/* Weight */}
      <td className="py-2.5 px-1 w-20">
        <div className="relative flex items-center">
          <MeasurementInput data-cargo-field="weight" compact label="Poids" quantity="mass" storageUnit={parseMeasurementUnit(item.weight_unit || 'kg')} value={item.weight ?? null} nullable={false} min={0} onValueChange={v => handleUpdateItemField(index, 'weight', v ?? 0)} className="w-full text-xs font-mono border rounded-lg bg-white dark:bg-slate-900 px-1 py-1.5" />
        </div>
      </td>

      {/* Quantity */}
      <td className="w-28 border-x border-orange-100 bg-orange-50/70 px-1 py-2.5 dark:border-orange-900/40 dark:bg-orange-950/20">
        <div className="flex items-center gap-0.5">
          <div className="relative min-w-0 flex-1">
            <input
              type="number"
              min={1}
              step={1}
              value={item.quantity ?? 1}
              onChange={(e) => handleUpdateItemField(index, 'quantity', e.target.value === '' ? 0 : Number(e.target.value))}
              aria-label={`Quantité de la ligne ${index + 1}`}
              aria-invalid={!Number.isInteger(item.quantity ?? 1) || (item.quantity ?? 1) <= 0}
              className={`w-full text-xs font-mono font-extrabold text-right py-1.5 pl-1.5 pr-4 border-2 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${(!Number.isInteger(item.quantity ?? 1) || (item.quantity ?? 1) <= 0) ? 'border-red-300 dark:border-red-800 bg-red-50/30' : 'border-orange-300 bg-orange-50 dark:border-orange-700 dark:bg-orange-950/40'
                }`}
            />
            <span className="absolute right-1.5 text-[10px] font-medium text-slate-400 pointer-events-none">u.</span>
          </div>
          <div className="flex shrink-0 flex-col">
            <button
              type="button"
              aria-label="Augmenter la quantité"
              title="Augmenter la quantité"
              onClick={() => handleUpdateItemField(index, 'quantity', quantity + 1)}
              className="flex h-4 w-4 items-center justify-center rounded text-orange-700 hover:bg-orange-200 dark:text-orange-300 dark:hover:bg-orange-900/60"
            >
              <ChevronUpIcon className="h-3 w-3" />
            </button>
            <button
              type="button"
              aria-label="Diminuer la quantité"
              title="Diminuer la quantité"
              disabled={quantity <= 1}
              onClick={() => handleUpdateItemField(index, 'quantity', Math.max(1, quantity - 1))}
              className="flex h-4 w-4 items-center justify-center rounded text-orange-700 hover:bg-orange-200 disabled:cursor-not-allowed disabled:opacity-40 dark:text-orange-300 dark:hover:bg-orange-900/60"
            >
              <ChevronDownIcon className="h-3 w-3" />
            </button>
          </div>
        </div>
      </td>

      {/* CONTRAINTES Column */}
      <td className="py-2.5 px-2 w-[220px] min-w-[220px] whitespace-nowrap">
        <div className="flex flex-nowrap items-center gap-1 whitespace-nowrap">
          {/* Empilable */}
          <button
            type="button"
            onClick={() => handleUpdateItemField(index, 'stackable', !item.stackable)}
            title={item.stackable ? 'Empilable : OUI' : 'Empilable : NON'}
            className={`p-1.5 rounded-lg flex items-center transition-all ${item.stackable
              ? 'bg-orange-100 text-orange-700 dark:bg-orange-950/60 dark:text-orange-300 border border-orange-300/80 dark:border-orange-800'
              : 'bg-slate-100 text-slate-400 dark:bg-slate-800/80 dark:text-slate-500 border border-slate-200 dark:border-slate-700'
              }`}
          >
            <Layers className="w-4 h-4" />
          </button>

          {/* Au sol uniquement */}
          <button
            type="button"
            onClick={() => handleUpdateItemField(index, 'must_be_on_floor', !item.must_be_on_floor)}
            title={item.must_be_on_floor ? 'Au sol uniquement : OUI' : 'Au sol uniquement : NON'}
            className={`p-1.5 rounded-lg flex items-center transition-all ${item.must_be_on_floor
              ? 'bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300 border border-amber-300/80 dark:border-amber-800'
              : 'bg-slate-100 text-slate-400 dark:bg-slate-800/80 dark:text-slate-500 border border-slate-200 dark:border-slate-700'
              }`}
          >
            <LayerArrowDown className="w-4 h-4" />
          </button>

          {/* Pas au-dessus */}
          <button
            type="button"
            onClick={() => handleUpdateItemField(index, 'no_stack_above', !item.no_stack_above)}
            title={item.no_stack_above ? 'Rien au-dessus : OUI' : 'Rien au-dessus : NON'}
            className={`p-1.5 rounded-lg flex items-center transition-all ${item.no_stack_above
              ? 'bg-red-100 text-red-700 dark:bg-red-950/60 dark:text-red-300 border border-red-300/80 dark:border-red-800'
              : 'bg-slate-100 text-slate-400 dark:bg-slate-800/80 dark:text-slate-500 border border-slate-200 dark:border-slate-700'
              }`}
          >
            <LayerArrowUp className="w-4 h-4" />
          </button>

          {/* Rotation 3D */}
          <button
            type="button"
            onClick={() => {
              const nextRotatable = !item.rotatable
              handleUpdateItemField(index, 'rotatable', nextRotatable)
              if (!nextRotatable) {
                handleUpdateItemField(index, 'rotationOptions', { allowRotationX: false, allowRotationY: false, allowRotationZ: false, rotationStrategy: 'NO_STACKING' })
              } else {
                handleUpdateItemField(index, 'rotationOptions', { allowRotationX: true, allowRotationY: true, allowRotationZ: true, rotationStrategy: 'DEFAULT_6_ROTATIONS' })
              }
            }}
            title={item.rotatable ? 'Rotation 3D autorisée' : 'Rotation désactivée'}
            className={`p-1.5 rounded-lg flex items-center transition-all ${item.rotatable
              ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950/60 dark:text-indigo-300 border border-indigo-300/80 dark:border-indigo-800'
              : 'bg-slate-100 text-slate-400 dark:bg-slate-800/80 dark:text-slate-500 border border-slate-200 dark:border-slate-700'
              }`}
          >
            <Rotate3D className="w-4 h-4" />
          </button>

          {/* Palette */}
          <button
            type="button"
            onClick={() => handleUpdateItemField(index, 'requires_pallet', !item.requires_pallet)}
            title={item.requires_pallet ? 'Palettisation requise' : 'Pas de palettisation'}
            className={`p-1.5 rounded-lg flex items-center transition-all ${item.requires_pallet
              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-300/80 dark:border-emerald-800'
              : 'bg-slate-100 text-slate-400 dark:bg-slate-800/80 dark:text-slate-500 border border-slate-200 dark:border-slate-700'
              }`}
          >
            <Table className="w-4 h-4" />
          </button>

          {/* Température / Frigo */}
          <button
            type="button"
            onClick={() => handleUpdateItemField(index, 'temperature_controlled', !item.temperature_controlled)}
            title={item.temperature_controlled ? 'Contrôle de température : OUI' : 'Contrôle de température : NON'}
            className={`p-1.5 rounded-lg flex items-center transition-all ${item.temperature_controlled
              ? 'bg-cyan-100 text-cyan-700 dark:bg-cyan-950/60 dark:text-cyan-300 border border-cyan-300/80 dark:border-cyan-800'
              : 'bg-slate-100 text-slate-400 dark:bg-slate-800/80 dark:text-slate-500 border border-slate-200 dark:border-slate-700'
              }`}
          >
            <Thermometer className="w-4 h-4" />
          </button>

          {/* ADR / Dangereux */}
          <span
            role="img"
            aria-label={item.dangerous_goods || item.is_hazardous ? 'Produit dangereux, ADR' : 'Produit non dangereux'}
            title={item.dangerous_goods || item.is_hazardous
              ? `Produit dangereux (Classe ${item.adr_class || 'N/A'}, UN: ${item.un_number || 'N/A'})`
              : 'Produit non dangereux'}
            className={`inline-flex items-center justify-center rounded-lg border p-1.5 ${item.dangerous_goods || item.is_hazardous
              ? 'border-red-300/80 bg-red-100 text-red-700 dark:border-red-800 dark:bg-red-950/60 dark:text-red-300'
              : 'border-slate-200 bg-slate-100 text-slate-400 dark:border-slate-700 dark:bg-slate-800/80 dark:text-slate-500'
              }`}
          >
            <ExclamationTriangleIcon className="h-4 w-4" aria-hidden="true" />
          </span>
        </div>
      </td>

      {/* Group Field */}
      <td className="py-2.5 px-2 w-[150px] min-w-0">
        <div className="relative flex items-center">
          {item.keep_together_group && (
            <span
              className="w-2 h-2 rounded-full mr-1 flex-shrink-0 border border-slate-300 dark:border-slate-600"
              style={{ backgroundColor: getColorFromGroupName(item.keep_together_group) }}
            />
          )}
          <input
            list="available-groups"
            value={item.keep_together_group || ''}
            onChange={(e) => handleUpdateItemField(index, 'keep_together_group', e.target.value)}
            placeholder="ex: C-1458"
            className="w-full text-xs py-1.5 px-1.5 border border-slate-200 dark:border-slate-700/80 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-colors"
          />
        </div>
      </td>

      {/* Actions */}
      <td className="py-2.5 px-2 text-center w-[136px]">
        <div className="flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            type="button"
            onClick={() => handleOpenProductForm(index)}
            className={`p-1 rounded-lg text-[11px] font-semibold transition-colors border border-transparent hover:border-orange-200 dark:hover:border-orange-800 ${itemTags.length > 0
              ? 'text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20'
              : 'text-slate-400 dark:text-slate-500 hover:text-orange-600 dark:hover:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20'
              }`}
            title="Détails & Contraintes Avancées (ADR, Température, Palette, Poids max)"
          >
            <PencilIcon className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={() => handleDuplicateItem(index)}
            className="p-1 rounded-lg text-[11px] font-semibold text-slate-400 dark:text-slate-500 hover:text-blue-500 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors border border-transparent hover:border-blue-200 dark:hover:border-blue-800"
            title="Dupliquer l'article"
          >
            <DocumentDuplicateIcon className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={() => handleRemoveItem(index)}
            className="p-1 rounded-lg text-[11px] font-semibold text-slate-400 dark:text-slate-500 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors border border-transparent hover:border-red-200 dark:hover:border-red-800"
            title="Supprimer l'article"
          >
            <TrashIcon className="w-3.5 h-3.5" />
          </button>
        </div>
      </td>
    </tr>
  )
}

export default function OptimizationPage() {
  const { runId } = useParams<{ runId: string }>()
  const location = useLocation()
  return <OptimizationPageContent key={runId || location.state?.jobId || 'new'} />
}
function OptimizationPageContent() {
  const { runId } = useParams<{ runId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()
  const access = useAccess()
  const optimizationAccess = access.resource('optimization')
  const equipmentAccess = access.resource('vehicle')
  const canEditEquipment = optimizationAccess.write && equipmentAccess.update

  const isExistingMode = !!runId

  // ── DND Sensors and Handlers ───────────────────────────────────────────────
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setSelectedItemIndexes([])
      setFormData((prev) => {
        if (!prev.items) return prev;
        const oldIndex = prev.items.findIndex(i => (i._dndId || i.id) === active.id);
        const newIndex = prev.items.findIndex(i => (i._dndId || i.id) === over.id);
        if (oldIndex === -1 || newIndex === -1) return prev;

        let newItems = arrayMove(prev.items, oldIndex, newIndex);
        newItems = newItems.map((item, idx) => ({ ...item, unloading_order: idx + 1 }));
        return { ...prev, items: newItems };
      });
    }
  }, []);


  // ── Form state ─────────────────────────────────────────────────────────────
  const [formData, setFormData] = useState<Partial<OptimizationInput>>(
    () => defaultFormData(user?.tenantId)
  )
  const [formInitialized, setFormInitialized] = useState(!isExistingMode)
  const [stableJobId, setStableJobId] = useState<string | undefined>(
    location.state?.jobId
  )
  const [editingItemIndex, setEditingItemIndex] = useState<number | null>(null)
  const [selectedItemIndexes, setSelectedItemIndexes] = useState<number[]>([])
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set())
  const [groupDialogOpen, setGroupDialogOpen] = useState(false)
  const [newGroupName, setNewGroupName] = useState('')
  const [manageGroupsDialogOpen, setManageGroupsDialogOpen] = useState(false)
  const [editingGroupName, setEditingGroupName] = useState<string | null>(null)
  const [editedGroupName, setEditedGroupName] = useState('')

  // ── Cargo Tab Search & Import ──────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('')
  const [csvDialogOpen, setCsvDialogOpen] = useState(false)
  const [catalogDialogOpen, setCatalogDialogOpen] = useState(false)
  const [catalogAddition, setCatalogAddition] = useState<{ firstId: string; products: number; units: number } | null>(null)
  const [selectedVehicleDetails, setSelectedVehicleDetails] = useState<VehicleSettings | null>(null)
  const [vehicleSearchDialogOpen, setVehicleSearchDialogOpen] = useState(false)
  const [tractorSearchDialogOpen, setTractorSearchDialogOpen] = useState(false)
  const [presetError, setPresetError] = useState<string | null>(null)


  // Fetch favorite products for presets
  const { data: productsData, isLoading: productsLoading } = useQuery({
    queryKey: ['cargo-product-presets'],
    enabled: access.resource('product').read,
    queryFn: async () => {
      return await productService.getProducts({ page: 1, pageSize: 100 })
    },
    staleTime: 0,
  })

  // Transform favorite products into cargo presets
  const CARGO_PRESETS = useMemo(() => {
    const allProducts = productsData?.products || []
    const favoriteProducts = allProducts
      .filter(p => (p.favorite_rating || 0) > 0)
      .sort((a, b) => (b.favorite_rating || 0) - (a.favorite_rating || 0))

    return favoriteProducts.map(product => {
      const dims = product.length && product.width && product.height
        ? <Dimensions length={product.length} width={product.width} height={product.height} from={product.dimension_unit || 'mm'} />
        : 'Dimensions non définies'

      return {
        id: product.id,
        sku: product.sku || undefined,
        name: product.name,
        description: product.description || undefined,
        hs_code: product.hs_code || undefined,
        unit_of_measure: product.unit_of_measure || undefined,
        packaging_type: product.packaging_type || undefined,
        volume: product.volume ?? undefined,
        volume_unit: product.volume_unit || undefined,
        country_of_origin: product.country_of_origin || undefined,
        status: product.status || undefined,
        sub: dims,
        length: product.length ?? 0,
        width: product.width ?? 0,
        height: product.height ?? 0,
        weight: product.weight ?? 0,
        weight_unit: product.weight_unit || undefined,
        dimension_unit: product.dimension_unit || 'mm',
        quantity: product.quantity ?? 1,
        stackable: product.stackable ?? true,
        fragile: product.fragile ?? false,
        rotatable: product.rotatable ?? true,
        requires_pallet: product.requires_pallet ?? false,
        pallet_type: product.pallet_type || undefined,
        color: product.color || undefined,
        // Advanced fields
        no_stack_above: product.no_stack_above ?? false,
        keep_upright: product.keep_upright,
        must_be_on_floor: product.must_be_on_floor ?? false,
        keep_together_group: product.keep_together_group || undefined,
        min_support_ratio: product.min_support_ratio ?? undefined,
        center_of_gravity_x: product.center_of_gravity_x ?? null,
        center_of_gravity_y: product.center_of_gravity_y ?? null,
        center_of_gravity_z: product.center_of_gravity_z ?? null,
        max_stack_weight: product.max_stack_weight ?? undefined,
        max_stack_layer: product.max_stack_layer ?? undefined,
        // Pallet fields
        max_pallet_height_mm: product.max_pallet_height_mm ?? undefined,
        pallet_custom_length: product.pallet_custom_length ?? undefined,
        pallet_custom_width: product.pallet_custom_width ?? undefined,
        max_items_per_pallet: product.max_items_per_pallet ?? undefined,
        pallet_board_height_mm: product.pallet_board_height_mm ?? undefined,
        pallet_empty_weight_kg: product.pallet_empty_weight_kg ?? undefined,
        pallet_color: product.pallet_color || undefined,
        max_pallet_weight_kg: product.max_pallet_weight_kg ?? undefined,
        // Rotation fields
        allow_rotation_x: product.allow_rotation_x ?? true,
        allow_rotation_y: product.allow_rotation_y ?? true,
        allow_rotation_z: product.allow_rotation_z ?? true,
        rotation_strategy: product.rotation_strategy || 'DEFAULT_6_ROTATIONS',
        // ADR fields
        dangerous_goods: product.is_dangerous_goods ?? false,
        adr_class: product.dangerous_goods_class || undefined,
        un_number: product.un_number || undefined,
        // Temperature fields
        temperature_controlled: product.temperature_controlled ?? false,
        min_temperature: product.min_temperature ?? undefined,
        max_temperature: product.max_temperature ?? undefined,
        temperature_unit: product.temperature_unit || undefined,
        // Unloading order
        unloading_order: product.unloading_order ?? undefined,
        icon: '📦',
        favorite_rating: product.favorite_rating ?? 0
      }
    })
  }, [productsData])

  const handleAddPresetItem = useCallback((preset: typeof CARGO_PRESETS[0]) => {
    try {
      const items = formData.items || []
      const newItem = createCargoItemFromPreset(
        preset,
        items,
        uuidv4,
        PREMIUM_COLORS[items.length % PREMIUM_COLORS.length],
      )
      setFormData(prev => ({ ...prev, items: [...(prev.items || []), newItem] }))
      setPresetError(null)
    } catch (error) {
      setPresetError(`Impossible d’ajouter ce favori : ${(error as Error).message}`)
    }
  }, [formData.items])

  const handleBatchUpdateSelected = useCallback((patch: Partial<Item3D>) => {
    setFormData(prev => {
      const newItems = [...(prev.items || [])]
      selectedItemIndexes.forEach(idx => {
        if (newItems[idx]) {
          newItems[idx] = { ...newItems[idx], ...patch }
        }
      })
      return { ...prev, items: newItems }
    })
  }, [selectedItemIndexes])

  const handleImportCsvItems = useCallback((importedItems: Item3D[], mode: 'append' | 'replace') => {
    setFormData(prev => {
      const current = prev.items || []
      if (mode === 'replace') {
        return { ...prev, items: importedItems }
      } else {
        const maxOrder = current.reduce((max, item) => {
          const order = Number(item.unloading_order) || 0
          return order > max ? order : max
        }, 0)
        const adjusted = importedItems.map((item, idx) => ({
          ...item,
          unloading_order: maxOrder + idx + 1
        }))
        return { ...prev, items: [...current, ...adjusted] }
      }
    })
  }, [])

  // ── Tab state ──────────────────────────────────────────────────────────────
  const [editingProjectInfo, setEditingProjectInfo] = useState(false)
  const [showValidation, setShowValidation] = useState(false)
  const [issueFocus, setIssueFocus] = useState<PreparationIssue | null>(null)
  const [activeMainTab, setActiveMainTab] = useState<MainTab>(() => {
    if (['cargaison', 'equipement', 'regles'].includes(location.state?.initialTab)) return location.state.initialTab as MainTab
    return 'cargaison'
  })
  const changePreparationTab = (tab: MainTab) => {
    const invalid = findInvalidPreparationInput()
    if (invalid) { invalid.reportValidity(); invalid.focus(); return }
    setActiveMainTab(tab)
    document.getElementById(`maintab-${tab}`)?.focus()
  }
  const searchProfileSelectRef = useRef<HTMLSelectElement>(null)
  const focusSearchProfileRequested = useRef(false)
  useEffect(() => {
    if (activeMainTab === 'regles' && focusSearchProfileRequested.current) {
      const target = searchProfileSelectRef.current?.disabled ? document.getElementById('panel-regles') : searchProfileSelectRef.current
      target?.focus()
      target?.scrollIntoView({ block: 'center' })
      focusSearchProfileRequested.current = false
    }
  }, [activeMainTab])
  // ── ProductForm state for advanced constraints ───────────────────────────
  const [showProductFormModal, setShowProductFormModal] = useState(false)
  const [adrClasses, setAdrClasses] = useState<any[]>([])
  const [unNumbers, setUnNumbers] = useState<any[]>([])

  // ── Rules Tab Collapsible Sections ────────────────────────────────────────
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set(['constraintWeights']))

  const toggleSection = (sectionId: string) => {
    setCollapsedSections(prev => {
      const next = new Set(prev)
      if (next.has(sectionId)) {
        next.delete(sectionId)
      } else {
        next.add(sectionId)
      }
      return next
    })
  }

  // ── Live run data ──────────────────────────────────────────────────────────
  const { run, loading: runLoading, error: runError } = useOptimizationRunLive({
    runId: runId || '__disabled__',
    enabled: isExistingMode,
  })


  // ── Project data ─────────────────────────────────────────────────────────────
  const { data: project, error: projectError, refetch: refetchProject } = useQuery({
    queryKey: ['project', run?.job_id || stableJobId],
    queryFn: async () => {
      if (run?.job_id || stableJobId) {
        return await jobProjectApi.getProject((run?.job_id || stableJobId)!)
      }
      return null
    },
    enabled: !!(run?.job_id || stableJobId),
  })

  // ── Vehicles from VehicleSettings ───────────────────────────────────────────
  const { data: vehiclesData, isLoading: vehiclesLoading, isError: vehiclesError, refetch: refetchVehicles } = useQuery({
    queryKey: ['optimization-equipment-library'],
    queryFn: async () => {
      return { data: await collectAllVehicles(params => vehicleService.getVehicles(params)) }
    },
    enabled: activeMainTab === 'equipement' && equipmentAccess.read,
  })

  const vehicles = vehiclesData?.data || []

  // Transform favorite vehicles into vehicle presets
  const VEHICLE_PRESETS = useMemo(() => {
    const allVehicles = vehicles || []
    const favoriteVehicles = allVehicles
      .filter(v => (v.favorite_rating || 0) > 0)
      .sort((a, b) => (b.favorite_rating || 0) - (a.favorite_rating || 0))

    return favoriteVehicles.map(vehicle => ({
      id: vehicle.id,
      name: getVehicleDisplayName(vehicle),
      sub: `${vehicle.brand || ''} ${vehicle.model || ''} (${vehicle.type})`,
      vehicle: vehicle,
      favorite_rating: vehicle.favorite_rating || 0
    }))
  }, [vehicles])

  // ── Global Settings from Settings Service ─────────────────────────────────────
  const { data: settingsData, isLoading: settingsLoading, error: settingsError, refetch: refetchSettings } = useQuery({
    queryKey: ['settings'],
    enabled: access.resource('optimization_settings').read,
    queryFn: async () => {
      return await settingsService.getSettings()
    },
    staleTime: 0, // Toujours chercher les données fraîches pour que les presets modifiés dans Settings soient visibles
  })

  const [draftSystem, setDraftSystem] = useState<MeasurementSystem | null>(null)
  const [measurementError, setMeasurementError] = useState<string | null>(null)
  const [savingMeasurement, setSavingMeasurement] = useState(false)
  const [draftBaseline, setDraftBaseline] = useState<string | null>(null)
  const draftInteracted = useRef(false)
  const markDraftInteraction = () => { draftInteracted.current = true }
  const draftSignature = JSON.stringify({ formData, draftSystem: !stableJobId && !isExistingMode ? draftSystem : null })
  useEffect(() => {
    if (formInitialized && !settingsLoading && (!draftInteracted.current || draftBaseline === null)) setDraftBaseline(draftSignature)
  }, [formInitialized, settingsLoading, draftBaseline, draftSignature])
  useUnsavedChangesWarning(savingMeasurement || (optimizationAccess.write && draftBaseline !== null && draftSignature !== draftBaseline))
  const projectSystem = resolveMeasurementSystem(draftSystem ?? (project
    ? project.measurement_system
    : isExistingMode ? run?.input?.measurement_system
      : settingsData?.tenantConfig.measurement_system ?? settingsData?.defaults.measurement_system))
  const changeProjectSystem = async (system: MeasurementSystem) => {
    setSavingMeasurement(true)
    setMeasurementError(null)
    try {
      const jobId = project?.job_id || stableJobId || run?.job_id
      if (jobId) {
        await jobProjectApi.updateMeasurementSystem(jobId, system)
        await refetchProject()
      }
      setDraftSystem(system)
    } catch {
      setMeasurementError('Le système de mesure n’a pas pu être enregistré. Réessayez.')
    } finally {
      setSavingMeasurement(false)
    }
  }

  // Merge defaults and tenantConfig
  const globalSettings = useMemo<OptimizationSettings>(() => {
    if (!settingsData) return {}
    return {
      limits: {
        ...(settingsData.defaults?.limits || {}),
        ...(settingsData.tenantConfig?.limits || {}),
      },
      thresholds: {
        stability: {
          ...(settingsData.defaults?.thresholds?.stability || {}),
          ...(settingsData.tenantConfig?.thresholds?.stability || {}),
        },
        axleLoadSafetyMarginPercent: settingsData.tenantConfig?.thresholds?.axleLoadSafetyMarginPercent ?? settingsData.defaults?.thresholds?.axleLoadSafetyMarginPercent ?? 0.05,
        enforceAxleLimits: settingsData.tenantConfig?.thresholds?.enforceAxleLimits ?? settingsData.defaults?.thresholds?.enforceAxleLimits ?? true
      },
      constraintWeights: {
        ...(settingsData.defaults?.constraintWeights || {}),
        ...(settingsData.tenantConfig?.constraintWeights || {})
      },
      optimizationWeights: {
        ...(settingsData.defaults?.optimizationWeights || {}),
        ...(settingsData.tenantConfig?.optimizationWeights || {})
      },
      optimizationPresets: {
        ...(settingsData.defaults?.optimizationPresets || {}),
        ...(settingsData.tenantConfig?.optimizationPresets || {})
      },
      defaultPreset: settingsData.tenantConfig?.defaultPreset ?? settingsData.defaults?.defaultPreset
    }
  }, [settingsData])

  const resolveOptimizationPresetId = useCallback(() => {
    return formData.optimizationPresetId ||
      settingsData?.tenantConfig?.defaultPreset ||
      settingsData?.defaults?.defaultPreset ||
      'standard'
  }, [
    formData.optimizationPresetId,
    settingsData?.tenantConfig?.defaultPreset,
    settingsData?.defaults?.defaultPreset
  ])

  const resolveOptimizationWeights = useCallback(() => {
    if (formData.optimizationWeights) return formData.optimizationWeights

    const presetId = resolveOptimizationPresetId()
    const mergedPresets = {
      ...(settingsData?.defaults?.optimizationPresets || {}),
      ...(settingsData?.tenantConfig?.optimizationPresets || {}),
    }

    return mergedPresets[presetId]?.weights ||
      settingsData?.tenantConfig?.optimizationWeights ||
      settingsData?.defaults?.optimizationWeights ||
      globalSettings.optimizationWeights
  }, [
    formData.optimizationWeights,
    resolveOptimizationPresetId,
    settingsData,
    globalSettings.optimizationWeights
  ])

  // Fetch ADR data for ProductForm
  useEffect(() => {
    const fetchADRData = async () => {
      try {
        const [classes, unNums] = await Promise.all([
          adrReferenceService.getADRClasses(),
          adrReferenceService.getUNNumbers()
        ])
        setAdrClasses(classes || [])
        setUnNumbers(unNums || [])
      } catch (err) {
        console.error('Failed to fetch ADR data:', err)
      }
    }
    fetchADRData()
  }, [])

  // Initialiser constraintEngineConfig.weights depuis globalSettings si non défini
  useEffect(() => {
    if (globalSettings.constraintWeights && !formData.constraintEngineConfig?.weights) {
      const weights = globalSettings.constraintWeights
      setFormData(prev => ({
        ...prev,
        constraintEngineConfig: {
          ...prev.constraintEngineConfig,
          weights: {
            stability: weights.stability ?? 1,
            weight: weights.weight ?? 1,
            adr: weights.adr ?? 1,
            stackability: weights.stackability ?? 1,
            rotation: weights.rotation ?? 1,
            accessibility: weights.accessibility ?? 1,
            centerOfGravity: weights.centerOfGravity ?? 1,
            // dynamicStability remplacé par les 3 métriques atomiques
            rollover: weights.rollover ?? 1,
            yaw: weights.yaw ?? 0.5,
            roll: weights.roll ?? 0.8,
            penaltyScale: weights.penaltyScale ?? 1
          }
        }
      }))
    }
  }, [globalSettings.constraintWeights, formData.constraintEngineConfig?.weights])

  // Initialiser optimizationPresetId et optimizationWeights depuis les settings globaux
  useEffect(() => {
    const defaultPreset = settingsData?.tenantConfig?.defaultPreset ?? settingsData?.defaults?.defaultPreset ?? 'standard'
    if (!formData.optimizationPresetId) {
      setFormData(prev => ({
        ...prev,
        optimizationPresetId: defaultPreset
      }))
    }
  }, [settingsData?.tenantConfig?.defaultPreset, settingsData?.defaults?.defaultPreset, formData.optimizationPresetId])

  useEffect(() => {
    // Recalculer les poids à chaque fois que les settings changent (ex: après une sauvegarde dans Settings)
    // afin de ne pas conserver des poids périmés en mémoire.
    const presetWeights = resolveOptimizationWeights()
    if (presetWeights && !formData.optimizationWeights) {
      setFormData(prev => ({
        ...prev,
        optimizationWeights: presetWeights
      }))
    }
  }, [resolveOptimizationWeights, formData.optimizationWeights])

  // Initialiser constraintEngineConfig.thresholds.maxCenterOfGravityDeviation depuis globalSettings si non défini
  useEffect(() => {
    const maxDeviation = globalSettings.thresholds?.stability?.maxCenterOfGravityDeviation ?? 0.1
    if (!formData.constraintEngineConfig?.thresholds?.maxCenterOfGravityDeviation) {
      setFormData(prev => ({
        ...prev,
        constraintEngineConfig: {
          ...prev.constraintEngineConfig,
          thresholds: {
            ...(prev.constraintEngineConfig?.thresholds || {}),
            maxCenterOfGravityDeviation: maxDeviation
          }
        }
      }))
    }
  }, [globalSettings.thresholds?.stability?.maxCenterOfGravityDeviation, formData.constraintEngineConfig?.thresholds?.maxCenterOfGravityDeviation])

  // Initialiser constraints.axleLoadSafetyMarginPercent depuis globalSettings si non défini
  useEffect(() => {
    const axleSafetyMargin = globalSettings.thresholds?.axleLoadSafetyMarginPercent ?? 0.05
    if (formData.constraints?.axleLoadSafetyMarginPercent === undefined) {
      setFormData(prev => ({
        ...prev,
        constraints: {
          ...prev.constraints,
          axleLoadSafetyMarginPercent: axleSafetyMargin
        }
      }))
    }
  }, [globalSettings.thresholds?.axleLoadSafetyMarginPercent, formData.constraints?.axleLoadSafetyMarginPercent])

  // Handler to pre-fill equipment form from selected vehicle
  const handleSelectVehicle = useCallback((vehicle: VehicleSettings) => {
    setSelectedVehicleDetails(vehicle)
    setFormData(prev => {
      const nextAxleConfig = (vehicle as any).axle_configuration
      const nextPhysicsType = typeof nextAxleConfig === 'object' ? nextAxleConfig?.physicsType : null
      const nextIsSemiTrailer = vehicle.type === 'SEMI_TRAILER' || nextPhysicsType === 'tractor_semitrailer'
      const isContainer = vehicle.type === 'CONTAINER'
      const sameTrailer = prev.vehicle?.id && prev.vehicle.id === vehicle.id
      const preserveCombination = nextIsSemiTrailer && sameTrailer

      return {
        ...prev,
        loadSpace: {
          length: vehicle.length || prev.loadSpace?.length || 1000,
          width: vehicle.width || prev.loadSpace?.width || 200,
          height: vehicle.height || prev.loadSpace?.height || 200,
          maxWeight: vehicle.capacity_weight || prev.loadSpace?.maxWeight,
          emptyWeight: vehicle.empty_weight ?? null,
          maxTotalWeight: vehicle.max_weight_permitted ?? null,
          is_hazardous_compatible: vehicle.is_hazardous_compatible || false,
        } as LoadSpace3D,
        vehicle: {
          ...prev.vehicle,
          id: vehicle.id,
          registration_number: vehicle.registration_number,
          type: vehicle.type,
          brand: vehicle.brand,
          model: vehicle.model,
          vehicle_subtype: vehicle.vehicle_subtype,
          container_type: (vehicle as any).container_type,
          max_weight: vehicle.capacity_weight,
          empty_weight: vehicle.empty_weight ?? null,
          max_weight_permitted: vehicle.max_weight_permitted ?? null,
          max_combination_weight: vehicle.max_combination_weight ?? null,
          mass_contract_version: vehicle.mass_contract_version ?? null,
          mass_provenance: vehicle.mass_provenance ?? null,
          dimensions: {
            length: vehicle.length,
            width: vehicle.width,
            height: vehicle.height,
          },
          // Ne jamais hériter des essieux ou de la sellette du véhicule précédent.
          // Un conteneur sélectionné est une unité de chargement sans châssis roulant.
          axle_configuration: isContainer ? null : (nextAxleConfig ?? null),
          fifth_wheel: isContainer ? null : ((vehicle as any).fifth_wheel ?? null),
          obstacles: isContainer ? [] : ((vehicle as any).obstacles ?? []),
          has_reefer_unit: (vehicle as any).has_reefer_unit || false,
          reefer_temperature_range: (vehicle as any).reefer_temperature_range || null,
        } as Vehicle,
        tractor: preserveCombination ? prev.tractor : null,
        vehicleCombination: preserveCombination
          ? synchronizeVehicleCombination(prev.vehicleCombination, {
            id: vehicle.id,
            axle_configuration: nextAxleConfig ?? null,
            fifth_wheel: (vehicle as any).fifth_wheel ?? null,
          } as Vehicle, prev.tractor)
          : undefined,
        constraints: {
          ...prev.constraints,
          maxWeight: vehicle.capacity_weight || prev.constraints?.maxWeight,
          maxStackHeight: vehicle.height || prev.constraints?.maxStackHeight,
          maxFloorPressure: optionalConstraintNumber(vehicle.max_floor_pressure ?? prev.constraints?.maxFloorPressure),
          pressureCellTargetSize: optionalConstraintNumber(vehicle.pressure_cell_target_size ?? prev.constraints?.pressureCellTargetSize),
        },
        constraintEngineConfig: {
          ...prev.constraintEngineConfig,
          ...(((vehicle as any).target_cg_x != null || (vehicle as any).target_cg_y != null || prev.constraintEngineConfig?.targetCG)
            ? {
              targetCG: {
                x: (vehicle as any).target_cg_x ?? prev.constraintEngineConfig?.targetCG?.x,
                y: (vehicle as any).target_cg_y ?? prev.constraintEngineConfig?.targetCG?.y,
              },
            }
            : {}),
          thresholds: {
            ...(prev.constraintEngineConfig?.thresholds || {}),
            maxCenterOfGravityDeviation: (vehicle as any).max_cg_deviation ?? prev.constraintEngineConfig?.thresholds?.maxCenterOfGravityDeviation ?? 0.2,
          },
        },
      }
    }
    )
  }, [])

  // Handler to select tractor for semi-trailer
  const handleSelectTractor = useCallback((tractor: VehicleSettings) => {
    setFormData(prev => {
      const trailerId = prev.vehicle?.id || ''
      const axleConfig = prev.vehicle?.axle_configuration
      const kingPinPosition = (typeof axleConfig === 'object' ? axleConfig?.kingPinPosition : undefined)
        ?? (prev.vehicle as any)?.fifth_wheel?.position ?? 0
      const tractorAxleConfig = (tractor as any).axle_configuration
      const fifthWheelPosition = tractorAxleConfig?.fifthWheelPosition ?? (tractor as any).fifthWheelPosition ?? 1200
      const fifthWheelMaxLoad = tractorAxleConfig?.fifthWheelMaxLoad ?? (tractor as any).fifthWheelMaxLoad ?? 12000
      // Les dimensions de VehicleSettings et celles des presets sont déjà en mm.
      // La vue 3D attend elle aussi des mm dans `dimensions`: toute conversion ici
      // agrandirait le tracteur d'un facteur 10.
      const dimensionInMm = (value: number | null | undefined) => {
        const millimeters = Number(value)
        return Number.isFinite(millimeters) && millimeters > 0 ? millimeters : undefined
      }

      return {
        ...prev,
        loadSpace: prev.loadSpace ? {
          ...prev.loadSpace,
          maxTotalWeight: prev.tractor?.id === tractor.id ? prev.vehicleCombination?.maxTotalWeight ?? null : null,
        } : undefined,
        tractor: {
          id: tractor.id,
          registration_number: tractor.registration_number,
          type: tractor.type,
          brand: tractor.brand,
          model: tractor.model,
          vehicle_subtype: tractor.vehicle_subtype,
          empty_weight: tractor.empty_weight ?? null,
          max_weight_permitted: tractor.max_weight_permitted ?? null,
          max_combination_weight: tractor.max_combination_weight ?? null,
          mass_contract_version: tractor.mass_contract_version ?? null,
          mass_provenance: tractor.mass_provenance ?? null,
          length: tractor.length,
          width: tractor.width,
          height: tractor.height,
          dimensions: {
            length: dimensionInMm(tractor.length),
            width: dimensionInMm(tractor.width),
            height: dimensionInMm(tractor.height),
          },
          axle_configuration: tractorAxleConfig || null,
          fifthWheelPosition,
          fifthWheelMaxLoad,
        } as Vehicle,
        vehicleCombination: {
          type: 'tractor_semitrailer',
          trailerVehicleId: trailerId,
          tractorVehicleId: tractor.id || '',
          maxTotalWeight: prev.tractor?.id === tractor.id ? prev.vehicleCombination?.maxTotalWeight ?? null : null,
          coupling: {
            trailerKingPinPosition: kingPinPosition,
            tractorFifthWheelPosition: fifthWheelPosition,
            fifthWheelMaxLoad,
          },
        } as any,
      }
    })
  }, [])

  // Pre-fill form from existing run on first load
  useEffect(() => {
    if (run && !formInitialized) {
      // Ensure each item has a unique _dndId for drag-and-drop
      const itemsWithIds = (run.input?.items ?? []).map((item: Item3D) => ({
        ...item,
        _dndId: item._dndId || uuidv4()
      }))

      const existingJobId = run.input?.jobId ?? (run as any).jobId ?? (run as any).job_id ?? null

      const existingInput = measurementInput(run.input)
      const existingVehicle = existingInput.vehicle

      setFormData({
        tenantId: run.input?.tenantId ?? (run as any).tenantId ?? user?.tenantId ?? null,
        jobId: existingJobId,
        measurement_system: run.input?.measurement_system ?? 'metric',
        loadSpace: existingInput.loadSpace,
        items: itemsWithIds,
        constraints: existingInput.constraints,
        options: { ...run.input?.options, searchProfile: run.input?.options?.searchProfile ?? DEFAULT_OPTIMIZATION_SEARCH_PROFILE },
        constraintEngineConfig: run.input?.constraintEngineConfig,
        optimizationPresetId: run.input?.optimizationPresetId,
        optimizationWeights: run.input?.optimizationWeights,
        vehicle: existingVehicle,
        tractor: existingInput.tractor ?? null,
        vehicleCombination: existingInput.vehicleCombination,
      })

      // Set selectedVehicleDetails if vehicle exists in the run
      if (existingVehicle) {
        setSelectedVehicleDetails(existingVehicle as VehicleSettings)
      }

      // Preserve the stable job_id for subsequent recalculations
      if (existingJobId) {
        setStableJobId(existingJobId)
      }
      setFormInitialized(true)

    }
  }, [run, formInitialized, location.state?.initialTab, user?.tenantId])

  // The Viewer is the only results page. Explicit edit navigation keeps the
  // existing form and project units without creating a second results surface.
  useEffect(() => {
    if (run?.status === 'completed' && run.result && !location.state?.editMode) {
      navigate(`/optimization/${run.id}/viewer`, { replace: true })
    }
  }, [run?.id, run?.status, run?.result, location.state?.editMode, navigate])

  // ── Mutations ──────────────────────────────────────────────────────────────
  const launchLock = useRef(false)
  const createMutation = useMutation({
    mutationFn: async (data: OptimizationInput) => {
      // For new projects, create a project first
      if (!stableJobId && !isExistingMode) {
        const project = await jobProjectApi.createProject({
          reference: `PROJ-${Date.now()}`,
          name: 'Nouveau projet de chargement',
          measurement_system: data.measurement_system,
          description: 'Projet créé automatiquement',
          tenantId: user?.tenantId || undefined,
        })
        setStableJobId(project.job_id)
        data.jobId = project.job_id
      } else {
        // Use existing stable job_id or fallback to jobId from data
        data.jobId = stableJobId || data.jobId
      }

      return optimizationApi.createRun({ ...data, items: uniqueCargoLineIds(data.items) })
    },
    onSettled: () => { launchLock.current = false },
    onSuccess: (response) => {
      const newRunId = response.run?.id
      if (newRunId) {
        navigate(`/optimization/${newRunId}`, { state: { initialTab: 'cargaison' } })
      }
    },
  })

  const cancelMutation = useMutation({
    mutationFn: () => optimizationApi.cancelRun(runId!),
  })

  // ── Item handlers ──────────────────────────────────────────────────────────
  const handleUpdateItem = useCallback((index: number, updatedItem: Item3D) => {
    setFormData(prev => {
      const newItems = [...(prev.items || [])]
      newItems[index] = updatedItem
      return { ...prev, items: newItems }
    })
  }, [])

  const handleUpdateItemField = useCallback(<K extends keyof Item3D>(index: number, field: K, value: Item3D[K]) => {
    setFormData(prev => {
      const newItems = [...(prev.items || [])]
      newItems[index] = {
        ...newItems[index],
        [field]: value
      }
      return { ...prev, items: newItems }
    })
  }, [])

  const handleRemoveItem = useCallback((index: number) => {
    setSelectedItemIndexes(prev => prev.filter(i => i !== index).map(i => i > index ? i - 1 : i))
    setFormData((prev) => ({
      ...prev,
      items: prev.items?.filter((_, i) => i !== index) || [],
    }))
  }, [])

  const handleDuplicateItem = useCallback((index: number) => {
    setSelectedItemIndexes(prev => prev.map(i => i > index ? i + 1 : i))
    setFormData((prev) => {
      const items = prev.items || []
      const itemToClone = items[index]
      if (!itemToClone) return prev

      const maxOrder = items.reduce((max, item) => {
        const order = Number(item.unloading_order) || 0;
        return order > max ? order : max;
      }, 0);

      const clonedItem: Item3D = {
        ...itemToClone,
        id: (() => {
          const base = itemToClone.id || 'colis'
          let candidate = `${base}-copy`
          let number = 2
          while (items.some(item => item.id === candidate)) candidate = `${base}-copy-${number++}`
          return candidate
        })(),
        unloading_order: maxOrder + 1,
        color: PREMIUM_COLORS[(items.length) % PREMIUM_COLORS.length],
        _dndId: uuidv4()
      }
      return {
        ...prev,
        items: [...items.slice(0, index + 1), clonedItem, ...items.slice(index + 1)]
      }
    })
  }, [])

  const handleAddRow = useCallback(() => {
    setSearchQuery('')
    setCollapsedGroups(new Set())
    setFormData(prev => {
      const items = prev.items || []
      const maxOrder = items.reduce((max, item) => {
        const order = Number(item.unloading_order) || 0;
        return order > max ? order : max;
      }, 0);

      return {
        ...prev,
        items: [...items, { ...createNewItem(items.length), unloading_order: maxOrder + 1 }]
      }
    })
  }, [])

  const handleAddCatalogProducts = (selection: CatalogProductSelection[]) => {
    if (!optimizationAccess.write || !access.resource('product').read || createMutation.isPending || run?.status === 'queued' || run?.status === 'running') {
      throw new Error('L’ajout de produits est indisponible avec votre accès ou pendant le calcul.')
    }
    const added = createCargoItemsFromCatalog(selection, formData.items || [], uuidv4, PREMIUM_COLORS)
    setFormData(previous => ({ ...previous, items: [...(previous.items || []), ...added] }))
    setSearchQuery('')
    setCollapsedGroups(new Set())
    setCatalogAddition({ firstId: added[0].id, products: added.length, units: added.reduce((sum, item) => sum + (item.quantity ?? 1), 0) })
  }

  // Handle opening ProductForm modal
  const handleOpenProductForm = useCallback((index: number) => {
    setEditingItemIndex(index)
    setShowProductFormModal(true)
  }, [])

  const selectedItem = editingItemIndex !== null ? formData.items?.[editingItemIndex] : null

  // Convert Item3D to Product-like format for ProductForm
  const itemAsProduct = useMemo<Product | null>(() => {
    if (!selectedItem) return null

    return {
      id: selectedItem.product_id || undefined,
      sku: selectedItem.sku || selectedItem.id || '',
      name: selectedItem.product_name || selectedItem.description || '',
      description: selectedItem.product_description ?? selectedItem.description ?? '',
      hs_code: selectedItem.hs_code || '',
      unit_of_measure: selectedItem.unit_of_measure || 'PIECE',
      packaging_type: selectedItem.packaging_type || '',
      volume: selectedItem.volume ?? null,
      volume_unit: selectedItem.volume_unit || 'm3',
      country_of_origin: selectedItem.country_of_origin || '',
      status: selectedItem.status || 'active',
      favorite_rating: selectedItem.favorite_rating ?? 0,
      length: selectedItem.length || 0,
      width: selectedItem.width || 0,
      height: selectedItem.height || 0,
      dimension_unit: selectedItem.dimension_unit || 'mm',
      weight: selectedItem.weight || 0,
      weight_unit: selectedItem.weight_unit || 'kg',
      quantity: selectedItem.quantity || 1,
      color: selectedItem.color || '#000000',
      is_dangerous_goods: selectedItem.dangerous_goods || false,
      dangerous_goods_class: selectedItem.adr_class || '',
      un_number: selectedItem.un_number || '',
      temperature_controlled: selectedItem.temperature_controlled ?? false,
      min_temperature: selectedItem.min_temperature ?? null,
      max_temperature: selectedItem.max_temperature ?? null,
      temperature_unit: selectedItem.temperature_unit || 'C',
      stackable: selectedItem.stackable ?? true,
      fragile: selectedItem.fragile ?? false,
      rotatable: selectedItem.rotatable ?? true,
      allow_rotation_x: selectedItem.rotationOptions?.allowRotationX ?? true,
      allow_rotation_y: selectedItem.rotationOptions?.allowRotationY ?? true,
      allow_rotation_z: selectedItem.rotationOptions?.allowRotationZ ?? true,
      rotation_strategy: selectedItem.rotationOptions?.rotationStrategy as any,
      no_stack_above: selectedItem.no_stack_above ?? false,
      center_of_gravity_x: selectedItem.center_of_gravity_x,
      center_of_gravity_y: selectedItem.center_of_gravity_y,
      center_of_gravity_z: selectedItem.center_of_gravity_z,
      max_stack_weight: selectedItem.max_stack_weight,
      max_stack_layer: selectedItem.max_stack_layer,
      must_be_on_floor: selectedItem.must_be_on_floor ?? false,
      requires_pallet: selectedItem.requires_pallet ?? false,
      pallet_type: selectedItem.pallet_type as 'EUR' | 'EUR2' | 'CUSTOM' | undefined,
      max_pallet_height_mm: selectedItem.max_pallet_height_mm,
      pallet_custom_length: selectedItem.pallet_custom_length,
      pallet_custom_width: selectedItem.pallet_custom_width,
      max_items_per_pallet: selectedItem.max_items_per_pallet,
      pallet_board_height_mm: selectedItem.pallet_board_height_mm,
      pallet_empty_weight_kg: selectedItem.pallet_empty_weight_kg,
      pallet_color: selectedItem.pallet_color,
      max_pallet_weight_kg: selectedItem.max_pallet_weight_kg,
      min_support_ratio: selectedItem.min_support_ratio,
    } as Product
  }, [selectedItem])

  // Handle saving ProductForm changes
  const handleSaveProductForm = useCallback((updatedProduct: Partial<Product>) => {
    if (editingItemIndex === null || !selectedItem) return

    const optionalNumber = (value: number | null | undefined, fallback?: number): number | undefined => {
      if (value === undefined) return fallback
      if (value === null) return undefined
      return Number(value)
    }

    const nullableNumber = (value: number | null | undefined, fallback?: number | null): number | null | undefined => {
      if (value === undefined) return fallback
      if (value === null) return null
      return Number(value)
    }

    const updatedItem: Item3D = {
      ...selectedItem,
      product_name: updatedProduct.name ?? selectedItem.product_name ?? selectedItem.description ?? '',
      product_description: updatedProduct.description ?? selectedItem.product_description ?? null,
      description: updatedProduct.name ?? selectedItem.product_name ?? selectedItem.description ?? '',
      id: updatedProduct.sku || selectedItem.sku || selectedItem.id,
      product_id: updatedProduct.id?.toString() || selectedItem.product_id,
      sku: updatedProduct.sku ?? selectedItem.sku ?? selectedItem.id,
      hs_code: updatedProduct.hs_code ?? selectedItem.hs_code,
      unit_of_measure: updatedProduct.unit_of_measure ?? selectedItem.unit_of_measure,
      packaging_type: updatedProduct.packaging_type ?? selectedItem.packaging_type,
      volume: updatedProduct.volume ?? selectedItem.volume,
      volume_unit: updatedProduct.volume_unit ?? selectedItem.volume_unit,
      country_of_origin: updatedProduct.country_of_origin ?? selectedItem.country_of_origin,
      status: updatedProduct.status ?? selectedItem.status,
      favorite_rating: updatedProduct.favorite_rating ?? selectedItem.favorite_rating,
      length: updatedProduct.length !== undefined ? Number(updatedProduct.length) : selectedItem.length,
      width: updatedProduct.width !== undefined ? Number(updatedProduct.width) : selectedItem.width,
      height: updatedProduct.height !== undefined ? Number(updatedProduct.height) : selectedItem.height,
      dimension_unit: updatedProduct.dimension_unit || selectedItem.dimension_unit || 'mm',
      weight: updatedProduct.weight !== undefined && updatedProduct.weight !== null ? Number(updatedProduct.weight) : selectedItem.weight,
      weight_unit: updatedProduct.weight_unit || selectedItem.weight_unit || 'kg',
      quantity: updatedProduct.quantity !== undefined && updatedProduct.quantity !== null ? Number(updatedProduct.quantity) : selectedItem.quantity,
      color: updatedProduct.color || selectedItem.color,
      dangerous_goods: updatedProduct.is_dangerous_goods ?? selectedItem.dangerous_goods,
      is_hazardous: updatedProduct.is_dangerous_goods ?? selectedItem.is_hazardous,
      adr_class: updatedProduct.dangerous_goods_class || selectedItem.adr_class,
      un_number: updatedProduct.un_number || selectedItem.un_number,
      temperature_controlled: updatedProduct.temperature_controlled ?? selectedItem.temperature_controlled,
      min_temperature: updatedProduct.min_temperature ?? selectedItem.min_temperature,
      max_temperature: updatedProduct.max_temperature ?? selectedItem.max_temperature,
      temperature_unit: updatedProduct.temperature_unit || selectedItem.temperature_unit || 'C',
      stackable: updatedProduct.stackable ?? selectedItem.stackable,
      fragile: updatedProduct.fragile ?? selectedItem.fragile,
      rotatable: updatedProduct.rotatable ?? selectedItem.rotatable,
      rotationOptions: {
        allowRotationX: updatedProduct.allow_rotation_x ?? selectedItem.rotationOptions?.allowRotationX ?? true,
        allowRotationY: updatedProduct.allow_rotation_y ?? selectedItem.rotationOptions?.allowRotationY ?? true,
        allowRotationZ: updatedProduct.allow_rotation_z ?? selectedItem.rotationOptions?.allowRotationZ ?? true,
        rotationStrategy: (updatedProduct.rotation_strategy as any) || selectedItem.rotationOptions?.rotationStrategy || 'DEFAULT_6_ROTATIONS',
      },
      no_stack_above: updatedProduct.no_stack_above ?? selectedItem.no_stack_above,
      center_of_gravity_x: nullableNumber(updatedProduct.center_of_gravity_x, selectedItem.center_of_gravity_x),
      center_of_gravity_y: nullableNumber(updatedProduct.center_of_gravity_y, selectedItem.center_of_gravity_y),
      center_of_gravity_z: nullableNumber(updatedProduct.center_of_gravity_z, selectedItem.center_of_gravity_z),
      max_stack_weight: optionalNumber(updatedProduct.max_stack_weight, selectedItem.max_stack_weight),
      max_stack_layer: optionalNumber(updatedProduct.max_stack_layer, selectedItem.max_stack_layer),
      must_be_on_floor: updatedProduct.must_be_on_floor ?? selectedItem.must_be_on_floor,
      requires_pallet: updatedProduct.requires_pallet ?? selectedItem.requires_pallet,
      pallet_type: (updatedProduct.pallet_type as 'EUR' | 'EUR2' | 'CUSTOM') || selectedItem.pallet_type,
      max_pallet_height_mm: nullableNumber(updatedProduct.max_pallet_height_mm, selectedItem.max_pallet_height_mm),
      pallet_custom_length: nullableNumber(updatedProduct.pallet_custom_length, selectedItem.pallet_custom_length),
      pallet_custom_width: nullableNumber(updatedProduct.pallet_custom_width, selectedItem.pallet_custom_width),
      max_items_per_pallet: optionalNumber(updatedProduct.max_items_per_pallet, selectedItem.max_items_per_pallet),
      pallet_board_height_mm: nullableNumber(updatedProduct.pallet_board_height_mm, selectedItem.pallet_board_height_mm),
      pallet_empty_weight_kg: nullableNumber(updatedProduct.pallet_empty_weight_kg, selectedItem.pallet_empty_weight_kg),
      pallet_color: updatedProduct.pallet_color || selectedItem.pallet_color,
      max_pallet_weight_kg: nullableNumber(updatedProduct.max_pallet_weight_kg, selectedItem.max_pallet_weight_kg),
      min_support_ratio: optionalNumber(updatedProduct.min_support_ratio, selectedItem.min_support_ratio),
    }

    handleUpdateItem(editingItemIndex, updatedItem)
    setShowProductFormModal(false)
    setEditingItemIndex(null)
  }, [editingItemIndex, selectedItem, handleUpdateItem])

  const preparationIssues = useMemo(() => {
    const issues = optimizationReadiness(formData)
    formData.items?.forEach((item, itemIndex) => {
      const error = validateItemAdvancedFields(itemToAdvancedFormValues(item))
      if (error) issues.push({ tab: 'cargaison', itemIndex, message: `Ligne ${itemIndex + 1} · ${item.id} : ${error}` })
    })
    return issues
  }, [formData])
  const goToIssue = (issue: PreparationIssue) => {
    const invalid = findInvalidPreparationInput()
    if (invalid && issue.tab !== activeMainTab) { invalid.reportValidity(); invalid.focus(); return }
    setActiveMainTab(issue.tab)
    setSearchQuery('')
    setCollapsedGroups(new Set())
    if (issue.itemIndex != null && (!issue.field || issue.field === 'id')) { handleOpenProductForm(issue.itemIndex); return }
    setIssueFocus(issue)
  }
  useEffect(() => {
    if (!issueFocus) return
    const frame = requestAnimationFrame(() => {
      const row = issueFocus.itemIndex == null ? null : document.querySelector<HTMLElement>(`[data-cargo-row="${issueFocus.itemIndex}"]`)
      const labels: Record<string, string> = { length: 'Longueur', width: 'Largeur', height: 'Hauteur', weight: 'Poids' }
      const field = issueFocus.field && labels[issueFocus.field]
        ? row?.querySelector<HTMLElement>(`input[data-cargo-field="${issueFocus.field}"]`)
        : issueFocus.field === 'quantity' ? row?.querySelector<HTMLElement>('input[type="number"][step="1"]')
          : row?.querySelector<HTMLElement>('input[type="text"]')
      const target = field ?? document.getElementById(`panel-${issueFocus.tab}`)
      target?.focus({ preventScroll: true })
      target?.scrollIntoView({ block: 'center', behavior: 'smooth' })
      setIssueFocus(null)
    })
    return () => cancelAnimationFrame(frame)
  }, [issueFocus, activeMainTab])

  // ── Submit handler ─────────────────────────────────────────────────────────
  const handleSubmit = useCallback(() => {
    if (launchLock.current) return
    setShowValidation(true)
    const invalid = findInvalidPreparationInput()
    if (invalid) { invalid.reportValidity(); invalid.focus(); return }
    if (preparationIssues.length) { goToIssue(preparationIssues[0]); return }
    if (run?.status === 'queued' || run?.status === 'running' || !optimizationAccess.write || createMutation.isPending || savingMeasurement || projectError || settingsError || ((stableJobId || run?.job_id) && !project)) return
    if (settingsLoading && !settingsData) return
    const pressureLimit = optionalConstraintNumber(formData.constraints?.maxFloorPressure)

    const missingTractorForSemi = isSemiTrailerVehicle(formData.vehicle) && !formData.tractor
    if (missingTractorForSemi) {
      const shouldContinue = window.confirm(
        "Vous avez sélectionné une semi-remorque sans tracteur.\n\n" +
        "Le calcul de chargement va continuer, mais la répartition de charge sur les essieux du tracteur, la sellette et la combinaison complète ne pourra pas être validée correctement.\n\n" +
        "Voulez-vous lancer le calcul quand même ?"
      )
      if (!shouldContinue) return
    }

    const optimizationPresetId = resolveOptimizationPresetId()
    const optimizationWeights = resolveOptimizationWeights()

    if (!optimizationAccess.write) return
    // Legacy algorithm labels are not execution strategies in the current Beam Search engine.
    const calculationInput = { ...formData } as OptimizationInput
    delete calculationInput.algorithm
    launchLock.current = true
    createMutation.mutate({
      ...calculationInput,
      options: { ...formData.options, searchProfile: formData.options?.searchProfile ?? DEFAULT_OPTIMIZATION_SEARCH_PROFILE },
      measurement_system: projectSystem,
      jobId: stableJobId || formData.jobId || uuidv4(),
      optimizationPresetId,
      optimizationWeights,
      // Initialiser maxStackHeight avec la hauteur de l'équipement
      constraints: {
        ...(formData as OptimizationInput).constraints,
        maxFloorPressure: pressureLimit,
        enforceAxleLimits: resolveVehicleAxleLimitEnforcement(formData.constraints?.enforceAxleLimits, formData.vehicle?.axle_configuration, globalSettings.thresholds?.enforceAxleLimits),
        maxStackHeight: (formData as OptimizationInput).loadSpace?.height
      },
      // Injecter tenantSettings depuis les settings globaux
      tenantSettings: {
        saas_config: {
          limits: globalSettings.limits
        },
        thresholds: globalSettings.thresholds,
        optimizationWeights
      },
      // Injecter estimatedExecutionTimeMs et cpuBudget depuis settings
      estimatedExecutionTimeMs: globalSettings.limits?.max_execution_time_ms || null,
      cpuBudget: globalSettings.limits?.max_cpu_budget || null,
      // Injecter constraintEngineConfig pour configurer ConstraintEvaluationEngine
      constraintEngineConfig: {
        thresholds: {
          minSupportRatio: globalSettings.thresholds?.stability?.minSupportRatio || 0.6,
          enforceAxleLimits: resolveVehicleAxleLimitEnforcement(formData.constraints?.enforceAxleLimits, formData.vehicle?.axle_configuration, globalSettings.thresholds?.enforceAxleLimits),
          stability: {
            minSupportRatio: globalSettings.thresholds?.stability?.minSupportRatio || 0.6,
            maxCenterOfGravityDeviation: formData.constraintEngineConfig?.thresholds?.maxCenterOfGravityDeviation ?? globalSettings.thresholds?.stability?.maxCenterOfGravityDeviation ?? 0.2
          }
        } as any,
        // Utiliser weights depuis formData (initialisés depuis globalSettings) ou défaut
        weights: formData.constraintEngineConfig?.weights || {
          stability: 1,
          weight: 1,
          adr: 1,
          stackability: 1,
          rotation: 1,
          accessibility: 1,
          centerOfGravity: 1,
          // dynamicStability remplacé par les 3 métriques atomiques
          rollover: 1,
          yaw: 0.5,
          roll: 0.8,
          penaltyScale: 1
        },
        ...(formData.constraintEngineConfig?.targetCG ? { targetCG: formData.constraintEngineConfig.targetCG } : {})
      }
    })
  }, [preparationIssues, projectSystem, savingMeasurement, projectError, settingsError, project, run?.job_id, run?.status, stableJobId, formData, createMutation, globalSettings, resolveOptimizationPresetId, resolveOptimizationWeights, settingsLoading, settingsData, optimizationAccess.write])

  // ── Grouped items for collapsible display (with search) ─────────────────
  const groupedItems = useMemo(() => {
    const items = formData.items || []
    const groupMap = new Map<string, { groupName: string; items: { item: Item3D & { _dndId?: string }; globalIndex: number }[] }>()

    items.forEach((item, globalIndex) => {
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim()
        const matchSku = [item.id, item.sku].some(value => (value || '').toLowerCase().includes(q))
        const matchDesc = [item.description, item.product_name, item.product_description].some(value => (value || '').toLowerCase().includes(q))
        const matchGroup = (item.keep_together_group || '').toLowerCase().includes(q)
        if (!matchSku && !matchDesc && !matchGroup) return
      }

      const groupKey = item.keep_together_group?.trim() || '__ungrouped__'
      if (!groupMap.has(groupKey)) {
        groupMap.set(groupKey, {
          groupName: groupKey === '__ungrouped__' ? 'Sans groupe' : groupKey,
          items: [],
        })
      }
      const group = groupMap.get(groupKey)!
      group.items.push({ item, globalIndex })
    })

    return Array.from(groupMap.entries()).map(([key, value]) => ({ key, ...value }))
  }, [formData.items, searchQuery])

  const filteredItemsCount = useMemo(() => {
    return groupedItems.reduce((sum, g) => sum + g.items.length, 0)
  }, [groupedItems])

  // ── Available group names for dropdown ────────────────────────────────────
  const availableGroups = useMemo(() => {
    const items = formData.items || []
    const groups = new Set<string>()
    items.forEach(item => {
      if (item.keep_together_group?.trim()) {
        groups.add(item.keep_together_group.trim())
      }
    })
    return Array.from(groups).sort()
  }, [formData.items])

  const toggleGroupCollapse = useCallback((groupKey: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev)
      if (next.has(groupKey)) next.delete(groupKey)
      else next.add(groupKey)
      return next
    })
  }, [])

  // ── Computed values ────────────────────────────────────────────────────────
  const totalVolumeM3 = useMemo(() => {
    if (!formData.items?.length) return 0
    return formData.items.reduce((sum, item) => {
      return sum + (['length', 'width', 'height'] as const).reduce((v, k) => v * convertMeasurement(Number(item[k]), parseMeasurementUnit(item.dimension_unit || 'mm'), 'm'), item.quantity ?? 1)
    }, 0)
  }, [formData.items])

  const totalWeightKg = useMemo(() => {
    if (!formData.items?.length) return 0
    return formData.items.reduce((sum, item) => {
      return sum + (convertMeasurement(Number(item.weight || 0), parseMeasurementUnit(item.weight_unit || 'kg'), 'kg') * (item.quantity ?? 1))
    }, 0)
  }, [formData.items])

  const totalUnitsCount = useMemo(() => {
    return formData.items?.reduce((sum, item) => sum + (item.quantity ?? 1), 0) || 0
  }, [formData.items])

  // Check if vehicle is a semi-trailer (requires tractor)
  const isSemiTrailer = useMemo(() => {
    return isSemiTrailerVehicle(formData.vehicle)
  }, [formData.vehicle?.type, formData.vehicle?.axle_configuration])


  const runIsActive = run?.status === 'queued' || run?.status === 'running'
  const equipmentEditsLocked = createMutation.isPending || runIsActive || savingMeasurement
  const canLaunch = !runIsActive && optimizationAccess.write && !createMutation.isPending && !savingMeasurement && !projectError && !((stableJobId || run?.job_id) && !project) && !settingsError && !(settingsLoading && !settingsData)

  const launchUnavailable = runIsActive ? 'Un calcul est en attente ou en cours. Attendez le résultat ou annulez ce calcul.' : !optimizationAccess.write ? 'Votre accès permet uniquement la consultation.' : createMutation.isPending ? 'Lancement du calcul…' : savingMeasurement ? 'Enregistrement des unités…' : projectError ? 'Le projet est indisponible.' : settingsError ? 'Les paramètres de calcul sont indisponibles.' : ((stableJobId || run?.job_id) && !project) || (settingsLoading && !settingsData) ? 'Chargement de la configuration…' : null

  const selectedSearchProfile = formData.options?.searchProfile ?? DEFAULT_OPTIMIZATION_SEARCH_PROFILE
  const searchModeReminder = (
    <button
      type="button"
      onClick={() => {
        focusSearchProfileRequested.current = true
        changePreparationTab('regles')
      }}
      disabled={createMutation.isPending}
      aria-label={`Modifier le mode de recherche : ${SEARCH_PROFILE_LABELS[selectedSearchProfile]}`}
      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:border-blue-300 hover:text-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:border-blue-500 dark:hover:text-blue-300"
    >
      <AdjustmentsHorizontalIcon className="h-3.5 w-3.5" aria-hidden="true" />
      Recherche : {SEARCH_PROFILE_LABELS[selectedSearchProfile]}
    </button>
  )

  const planVersion = run?.plan_version
    ?? run?.result?.plan_version
    ?? run?.result?.optimization_metadata?.plan_version
    ?? run?.result?.optimizationMetadata?.plan_version

  // ── Loading / Error (existing mode only) ───────────────────────────────────
  if (isExistingMode && runLoading && !run) {
    return (
      <div className={opt.loadingCenter}>
        <div className="text-center">
          <div className={`${opt.spinner} mb-4`} />
          <p className={opt.loadingText}>Chargement de l&apos;optimisation...</p>
        </div>
      </div>
    )
  }

  if (isExistingMode && runError && !run) {
    return (
      <div className={opt.page}>
        <div className={opt.content}>
          <button
            type="button"
            onClick={() => navigate('/optimization')}
            className={opt.backBtn}
          >
            <ChevronLeftIcon className="w-4 h-4" /> Retour
          </button>
          <div className={opt.alertErrorBox}>
            <p className="font-semibold mb-2">Erreur</p>
            <p>{runError.message || "L'optimisation n'a pas pu être trouvée"}</p>
          </div>
        </div>
      </div>
    )
  }

  if ((stableJobId || run?.job_id) && !project) {
    return <div role={projectError ? 'alert' : 'status'} className="p-6">
      {projectError ? <>Impossible de charger le dossier. <button onClick={() => refetchProject()}>Réessayer</button></> : 'Chargement du dossier et de ses unités…'}
    </div>
  }
  if (!isExistingMode && !project && !settingsData) {
    return <div role={settingsError ? 'alert' : 'status'} className="p-6">
      {settingsError ? <>Impossible de charger les unités. <button onClick={() => refetchSettings()}>Réessayer</button></> : 'Chargement des unités…'}
    </div>
  }

  // ────────────────────────────────────────────────────────────────────────────
  // RENDER
  // ────────────────────────────────────────────────────────────────────────────

  return (
    <MeasurementProvider system={projectSystem}>
      <div className="optimization-workspace min-h-screen bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-100" style={{ minHeight: '100vh' }} onChangeCapture={markDraftInteraction} onPointerDownCapture={markDraftInteraction} onClickCapture={markDraftInteraction} onKeyDownCapture={event => { if (event.key !== 'Tab') markDraftInteraction() }}>
        {/* ── HEADER ─────────────────────────────────────────────────────────── */}
        <OptimizationProjectHeader
          name={project?.name || (isExistingMode ? `Optimisation #${run?.id ?? runId}` : 'Préparer le chargement')}
          reference={project?.reference}
          version={planVersion}
          requestedAt={run?.requestedAt}
          status={run?.status}
          statusLabel={run ? STATUS_LABELS[run.status] ?? run.status : 'À préparer'}
          onBack={() => navigate('/optimization')}
          onEdit={project && optimizationAccess.write ? () => setEditingProjectInfo(true) : undefined}
          onCancel={runIsActive && optimizationAccess.write ? () => cancelMutation.mutate() : undefined}
          cancelling={cancelMutation.isPending}
          onViewResult={run?.status === 'completed' && run.result ? () => navigate(`/optimization/${run.id}/viewer`) : undefined}
        />

        {/* ── RUNNING BANNER ─────────────────────────────────────────────────── */}
        {(run?.status === 'queued' || run?.status === 'running') && (
          <div
            className="bg-blue-50 dark:bg-blue-950/60 border-b border-blue-200 dark:border-blue-800"
            role="status"
            aria-live="polite"
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-3">
              <div className={`${opt.spinnerSm} flex-shrink-0`} aria-hidden="true" />
              <div className="flex-1 min-w-0">
                <span className={opt.alertRunningTitle}>{run?.status === 'queued' ? 'Calcul en attente de traitement… ' : 'Optimisation en cours… '}</span>
                <span className={opt.alertRunningText}>
                  Mise à jour automatique toutes les 3 s.
                </span>
              </div>
            </div>
            {/* Indeterminate progress bar */}
            <div className="h-0.5 bg-blue-100 dark:bg-blue-900 overflow-hidden">
              <div className="opt-progress-bar h-full bg-blue-500 dark:bg-blue-400" />
            </div>
          </div>
        )}

        {/* ── FAILED BANNER ──────────────────────────────────────────────────── */}
        {run?.status === 'failed' && run.error && (
          <div
            className="bg-red-50 dark:bg-red-950/60 border-b border-red-200 dark:border-red-800"
            role="alert"
            aria-live="assertive"
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
              <p className={`font-semibold text-sm ${opt.alertErrorTitle}`}>
                ⚠ Erreur lors de l&apos;optimisation
              </p>
              <p className={`text-sm ${opt.alertErrorText}`}>{run.error.message}</p>
            </div>
          </div>
        )}

        {/* ── MAIN TAB BAR ───────────────────────────────────────────────────── */}
        <div
          className="sticky top-0 z-10 mx-auto mt-3 max-w-7xl overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900"
        >
          <div className="p-1.5">
            <nav
              className="grid grid-cols-3 gap-1 sm:flex sm:gap-1.5"
              role="tablist"
              aria-label="Étapes de préparation du chargement"
              onKeyDown={event => {
                const tabs: MainTab[] = ['cargaison', 'equipement', 'regles']
                const index = tabs.indexOf(activeMainTab)
                const next = event.key === 'ArrowRight' ? tabs[(index + 1) % 3] : event.key === 'ArrowLeft' ? tabs[(index + 2) % 3] : event.key === 'Home' ? tabs[0] : event.key === 'End' ? tabs[2] : null
                if (next) { event.preventDefault(); changePreparationTab(next) }
              }}
            >
              {(
                [
                  {
                    id: 'cargaison' as MainTab,
                    label: 'Cargaison',
                    labelShort: 'Cargo',
                    icon: ArchiveBoxIcon,
                    disabled: false,
                    badge:
                      (formData.items?.length ?? 0) > 0
                        ? String(formData.items!.length)
                        : undefined as string | undefined,
                    badgeColor: 'blue' as 'blue' | 'green',
                  },
                  {
                    id: 'equipement' as MainTab,
                    label: 'Équipement',
                    labelShort: 'Équip.',
                    icon: TruckIcon,
                    disabled: false,
                    badge: undefined as string | undefined,
                    badgeColor: 'blue' as 'blue' | 'green',
                  },
                  {
                    id: 'regles' as MainTab,
                    label: 'Règles',
                    labelShort: 'Règles',
                    icon: AdjustmentsHorizontalIcon,
                    disabled: false,
                    badge: undefined as string | undefined,
                    badgeColor: 'blue' as 'blue' | 'green',
                  },

                ]
              ).map(({ id, label, labelShort, icon: Icon, disabled, badge, badgeColor }) => {
                const isActive = activeMainTab === id
                return (
                  <button
                    key={id}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    tabIndex={isActive ? 0 : -1}
                    aria-controls={`panel-${id}`}
                    id={`maintab-${id}`}
                    disabled={!!disabled}
                    onClick={() => !disabled && changePreparationTab(id)}
                    className={[
                      'relative inline-flex min-h-11 min-w-0 items-center justify-center gap-1.5 rounded-xl px-2 py-2.5 sm:gap-2 sm:px-5',
                      'border text-xs font-semibold transition-colors sm:text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500',
                      isActive
                        ? 'border-orange-200 bg-orange-50 text-orange-800 shadow-sm dark:border-orange-500/25 dark:bg-orange-500/10 dark:text-orange-300'
                        : disabled
                          ? 'border-transparent text-gray-300 dark:text-gray-600 cursor-not-allowed'
                          : 'border-transparent text-slate-500 hover:bg-slate-50 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100',
                    ].join(' ')}
                  >
                    <Icon className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                    <span className="hidden sm:inline">{label}</span>
                    <span className="sm:hidden">{labelShort}</span>
                    {badge && (
                      <span
                        className={[
                          'ml-0.5 px-1.5 py-0.5 text-xs rounded-full font-semibold leading-none',
                          badgeColor === 'green'
                            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300'
                            : 'bg-orange-100 text-orange-800 dark:bg-orange-500/15 dark:text-orange-200',
                          badge === '…' ? 'animate-pulse' : '',
                        ].join(' ')}
                        aria-label={`${label} : ${badge}`}
                      >
                        {badge}
                      </span>
                    )}
                  </button>
                )
              })}
            </nav>
          </div>
        </div>

        {/* ── PAGE CONTENT ───────────────────────────────────────────────────── */}
        <main className="mx-auto w-full max-w-7xl space-y-4 py-4 sm:space-y-5 sm:py-5" role="main" aria-label="Contenu de l'optimisation">

          {/* ══════════════════════════════════════════════════════════════════
            FORM TABS  (Cargaison · Équipement · Règles)
            Layout: 1-col full width with Top Bar
        ══════════════════════════════════════════════════════════════════ */}
          {showValidation && preparationIssues.length > 0 && <section aria-label="Points à corriger" className="rounded-xl border border-orange-200 bg-orange-50 p-3 text-sm text-orange-900 dark:border-orange-900 dark:bg-orange-950/30 dark:text-orange-200">
            <details open><summary className="cursor-pointer font-semibold">{preparationIssues.length} point(s) à compléter avant calcul</summary><ul className="mt-2 space-y-1">{preparationIssues.slice(0, 20).map((issue, index) => <li key={index}><button type="button" onClick={() => goToIssue(issue)} className="min-h-8 text-left text-xs underline underline-offset-2">{issue.message}</button></li>)}</ul>{preparationIssues.length > 20 && <p className="mt-2 text-xs">Les 20 premiers points sont affichés. Les suivants apparaîtront après correction.</p>}</details>
          </section>}
          {cancelMutation.isError && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200">L’annulation n’a pas pu être effectuée. Réessayez depuis le bouton Annuler.</p>}
          {createMutation.isError && <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200">Le calcul n’a pas pu être lancé : {createMutation.error instanceof Error ? createMutation.error.message : 'vérifiez votre connexion puis réessayez.'} Votre saisie est conservée.</div>}

          {(
            <fieldset disabled={activeMainTab === 'cargaison' && (createMutation.isPending || runIsActive || !optimizationAccess.write)} className="min-w-0 space-y-4 border-0 p-0">



              {/* ── FORM AREA ───────────────────────────────────────────── */}
              <div className="mx-auto w-full max-w-7xl space-y-4">

                {/* ─── TAB: CARGAISON ──────────────────────────────────── */}
                {activeMainTab === 'cargaison' && (
                  <div
                    id="panel-cargaison"
                    role="tabpanel"
                    tabIndex={-1}
                    aria-labelledby="maintab-cargaison"
                    className="opt-panel-enter w-full space-y-4"
                  >
                    <dl aria-label="Totaux de la cargaison" className="grid grid-cols-2 gap-2.5 lg:grid-cols-4">
                      {[
                        { label: 'Volume', value: totalVolumeM3 > 0 ? <Measure value={totalVolumeM3} from="m3" quantity="volume" /> : '—', icon: SquareDimensions, accent: '#0284c7' },
                        { label: 'Poids', value: totalWeightKg > 0 ? <Measure value={totalWeightKg} from="kg" quantity="mass" /> : '—', icon: Weight, accent: '#059669' },
                        { label: 'Unités', value: totalUnitsCount, total: formData.items?.length || 0, icon: CubeIcon, accent: '#ea580c' },
                        { label: 'Groupes', value: availableGroups.length, icon: Group, accent: '#7c3aed' },
                      ].map(stat => {
                        const Icon = stat.icon
                        return <div key={stat.label} className="min-w-0 rounded-2xl border border-slate-200/80 bg-white px-4 py-3.5 shadow-[0_2px_8px_-6px_rgba(15,23,42,0.14)] dark:border-slate-800 dark:bg-slate-900">
                          <dt className="flex items-center justify-between gap-2 text-xs font-medium text-slate-500 dark:text-slate-400">{stat.label}<Icon aria-hidden="true" className="h-4 w-4 shrink-0 opacity-80" style={{ color: stat.accent }} /></dt>
                          <dd className="mt-1.5 flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-xl font-semibold tracking-tight text-slate-900 tabular-nums dark:text-white">
                            <span>{stat.value}</span>{stat.total !== undefined && <span className="text-[11px] font-normal tracking-normal text-slate-500 dark:text-slate-400">{stat.total} ligne(s)</span>}
                          </dd>
                        </div>
                      })}
                    </dl>

                    <section aria-label="Ajouter des articles" className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                      <div className="grid grid-cols-2 gap-2 p-3 sm:flex sm:flex-wrap sm:items-center sm:p-3.5">
                        <button type="button" onClick={() => setCatalogDialogOpen(true)} disabled={!access.resource('product').read}
                          title={!access.resource('product').read ? 'La consultation du catalogue nécessite la permission de lecture des produits.' : 'Rechercher et sélectionner des produits du catalogue'}
                          className="col-span-2 inline-flex min-h-10 items-center justify-center gap-2 rounded-xl bg-orange-700 px-4 py-2.5 text-xs font-semibold text-white transition-colors hover:bg-orange-800 disabled:cursor-not-allowed disabled:opacity-40 sm:col-span-1">
                          <MagnifyingGlassIcon aria-hidden="true" className="h-4 w-4" />Catalogue produits
                        </button>
                        <button type="button" onClick={handleAddRow} title="Saisir un article manuellement" className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-xs font-semibold text-slate-700 transition-colors hover:border-orange-300 hover:bg-orange-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"><PlusIcon aria-hidden="true" className="h-4 w-4" />Ajouter</button>
                        <button type="button" onClick={() => setCsvDialogOpen(true)} className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-xs font-semibold text-slate-700 transition-colors hover:border-orange-300 hover:bg-orange-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"><ArrowUpTrayIcon aria-hidden="true" className="h-4 w-4" />Import CSV</button>
                        {availableGroups.length > 0 && <button type="button" onClick={() => setManageGroupsDialogOpen(true)} className="col-span-2 inline-flex min-h-10 items-center justify-center gap-2 rounded-xl border border-slate-200 px-3 py-2.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 sm:ml-auto"><Bars3Icon aria-hidden="true" className="h-4 w-4" />Groupes ({availableGroups.length})</button>}
                      </div>
                      {access.resource('product').read && (productsLoading || CARGO_PRESETS.length > 0) && <div className="flex items-center gap-2 overflow-x-auto border-t border-slate-100 px-3 py-2.5 dark:border-slate-800 sm:px-3.5">
                        <span className="inline-flex shrink-0 items-center gap-1.5 text-xs font-medium text-slate-500 dark:text-slate-400"><SparklesIcon aria-hidden="true" className="h-3.5 w-3.5 text-amber-500" />Favoris</span>
                        {productsLoading ? <span className="text-xs text-slate-400">Chargement…</span> : CARGO_PRESETS.map(preset => <button key={preset.id || preset.name} type="button" onClick={() => handleAddPresetItem(preset)} title={`Ajouter : ${preset.name}`} className="inline-flex min-h-8 shrink-0 items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-600 transition-colors hover:border-orange-300 hover:bg-orange-50 hover:text-orange-800 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:border-orange-700">{preset.name}</button>)}
                      </div>}
                    </section>

                    {catalogAddition && <div role="status" className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-1 py-1 text-xs text-emerald-700 dark:text-emerald-300">
                      <p className="min-w-0 flex-1">{catalogAddition.products} produit(s) ajouté(s) depuis le catalogue · {catalogAddition.units} unité(s).</p>
                      {(formData.items || []).some(item => item.id === catalogAddition.firstId) && <button type="button" onClick={() => goToIssue({ tab: 'cargaison', itemIndex: formData.items!.findIndex(item => item.id === catalogAddition.firstId), field: 'quantity', message: '' })} className="min-h-9 font-semibold underline">Voir les articles ajoutés</button>}
                      <button type="button" onClick={() => setCatalogAddition(null)} aria-label="Masquer la confirmation d’ajout" className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-200"><XMarkIcon aria-hidden="true" className="h-4 w-4" /></button>
                    </div>}
                    {presetError && (
                      <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/30 dark:text-red-300">
                        {presetError}
                      </div>
                    )}

                    {/* ── MAIN TABLE CONTAINER AVEC RECHERCHE ET FILTRES INTEGRÉS AU TABLEAU ── */}
                    <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">

                      {/* EN-TÊTE DISCRET INTEGRÉ DE RECHERCHE ET FILTRES (Directement rattaché au Tableau) */}
                      <div className="flex flex-col justify-between gap-3 border-b border-slate-100 bg-white p-3 dark:border-slate-800 dark:bg-slate-900 sm:flex-row sm:items-center sm:p-3.5">

                        {/* Recherche rapide directement liée à la table */}
                        <div className="flex items-center gap-2.5 flex-1 min-w-[220px] max-w-md">
                          <div className="relative flex-1">
                            <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input
                              type="text"
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                              aria-label="Rechercher dans la cargaison" placeholder="Rechercher SKU, description, groupe…"
                              className="min-h-10 w-full rounded-xl border border-slate-200 bg-slate-50/70 py-2 pl-9 pr-8 text-sm text-slate-900 placeholder-slate-400 transition-colors focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-500/15 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-100"
                            />
                            {searchQuery && (
                              <button
                                type="button"
                                onClick={() => setSearchQuery('')}
                                aria-label="Effacer la recherche dans la cargaison"
                                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                              >
                                <XMarkIcon className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>

                          {searchQuery && (
                            <span className="text-[11px] font-bold text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-950/50 border border-orange-200 dark:border-orange-900/60 px-2 py-1 rounded-md flex-shrink-0 font-mono">
                              {filteredItemsCount} / {formData.items?.length || 0} SKUs
                            </span>
                          )}
                        </div>
                      </div>


                      {/* STICKY BULK ACTIONS BAR (When items are selected) */}
                      {selectedItemIndexes.length > 0 && (
                        <div className="px-6 py-3 bg-orange-50 dark:bg-orange-950/40 border-b border-orange-200 dark:border-orange-900/60 flex flex-wrap items-center justify-between gap-3 animate-fade-in">
                          <div className="flex items-center gap-2">
                            <span className="w-2.5 h-2.5 rounded-full bg-orange-500 animate-pulse" />
                            <span className="text-xs font-bold text-orange-900 dark:text-orange-200">
                              {selectedItemIndexes.length} article{selectedItemIndexes.length > 1 ? 's' : ''} sélectionné{selectedItemIndexes.length > 1 ? 's' : ''}
                            </span>
                          </div>

                          <div className="flex items-center gap-2 flex-wrap">
                            <button
                              type="button"
                              onClick={() => {
                                setGroupDialogOpen(true)
                                setNewGroupName('')
                              }}
                              className="px-3 py-1.5 text-xs font-bold text-orange-700 dark:text-orange-300 bg-white dark:bg-slate-900 border border-orange-300 dark:border-orange-800 rounded-lg hover:bg-orange-100 dark:hover:bg-orange-900/40 transition-colors flex items-center gap-1"
                            >
                              Affecter un groupe
                            </button>

                            <button
                              type="button"
                              onClick={() => handleBatchUpdateSelected({ stackable: true })}
                              className="px-3 py-1.5 text-xs font-semibold text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                            >
                              Rendre Empilable
                            </button>

                            <button
                              type="button"
                              onClick={() => handleBatchUpdateSelected({ requires_pallet: true })}
                              className="px-3 py-1.5 text-xs font-semibold text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                            >
                              Activer Palette
                            </button>

                            <button
                              type="button"
                              onClick={() => {
                                const newItems = [...(formData.items || [])]
                                const maxOrder = newItems.reduce((max, item) => {
                                  const order = Number(item.unloading_order) || 0
                                  return order > max ? order : max
                                }, 0)

                                const clonedItems = selectedItemIndexes.map((idx, i) => {
                                  const itemToClone = newItems[idx]
                                  if (!itemToClone) return null
                                  return {
                                    ...itemToClone,
                                    id: itemToClone.id ? `${itemToClone.id}-copy` : '',
                                    unloading_order: maxOrder + i + 1,
                                    color: PREMIUM_COLORS[(newItems.length + i) % PREMIUM_COLORS.length],
                                    _dndId: uuidv4()
                                  }
                                }).filter(Boolean) as Item3D[]

                                setFormData(prev => ({
                                  ...prev,
                                  items: [...newItems, ...clonedItems]
                                }))
                                setSelectedItemIndexes([])
                              }}
                              className="px-3 py-1.5 text-xs font-bold text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors flex items-center gap-1"
                            >
                              <DocumentDuplicateIcon className="w-3.5 h-3.5" />
                              Dupliquer
                            </button>

                            <button
                              type="button"
                              onClick={() => {
                                if (window.confirm(`Supprimer les ${selectedItemIndexes.length} articles sélectionnés ?`)) {
                                  setFormData(prev => ({
                                    ...prev,
                                    items: prev.items?.filter((_, i) => !selectedItemIndexes.includes(i)) || []
                                  }))
                                  setSelectedItemIndexes([])
                                }
                              }}
                              className="px-3 py-1.5 text-xs font-bold text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/60 transition-colors flex items-center gap-1"
                            >
                              <TrashIcon className="w-3.5 h-3.5" />
                              Supprimer
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Table / Empty State */}
                      {groupedItems.length === 0 ? (
                        <div className="text-center py-16 px-4 bg-slate-50/40 dark:bg-slate-900/20">
                          <ArchiveBoxIcon className="w-16 h-16 mx-auto text-slate-300 dark:text-slate-600 stroke-[1.25] mb-4" />
                          <h3 className="text-base font-bold text-slate-900 dark:text-white">
                            {searchQuery ? 'Aucun article ne correspond à la recherche' : 'Aucun article dans la cargaison'}
                          </h3>
                          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 max-w-sm mx-auto">
                            {searchQuery
                              ? 'Essayez de modifier ou de réinitialiser la recherche pour afficher vos articles.'
                              : 'Ajoutez votre premier article ou importez un fichier CSV pour commencer.'}
                          </p>
                          <div className="mt-6 flex justify-center gap-3">
                            {searchQuery ? (
                              <button
                                type="button"
                                onClick={() => setSearchQuery('')}
                                className="px-4 py-2 text-xs font-bold text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl hover:bg-slate-100 transition-colors"
                              >
                                Réinitialiser la recherche
                              </button>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  onClick={() => setCsvDialogOpen(true)}
                                  className="px-4 py-2 text-xs font-bold text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl hover:bg-slate-100 transition-colors flex items-center gap-1.5"
                                >
                                  <ArrowUpTrayIcon className="w-4 h-4 text-orange-500" />
                                  Importer CSV
                                </button>
                                <button
                                  type="button"
                                  onClick={handleAddRow}
                                  className="px-4 py-2 text-xs font-bold text-white bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 rounded-xl transition-all shadow-xs"
                                >
                                  + Ajouter un article
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="relative w-full overflow-x-auto">
                          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                            <table className="min-w-[1376px] w-full text-left border-collapse">
                              <thead>
                                <tr className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-800">
                                  <th className="w-2 min-w-[8px] p-0"></th>
                                  <th className="py-3 px-4 w-8 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400"></th>
                                  <th className="py-3 px-4 w-10 text-center text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                    <input
                                      type="checkbox"
                                      aria-label="Sélectionner toute la cargaison"
                                      checked={(formData.items?.length || 0) > 0 && selectedItemIndexes.length === formData.items!.length}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setSelectedItemIndexes(formData.items?.map((_, i) => i) || [])
                                        } else {
                                          setSelectedItemIndexes([])
                                        }
                                      }}
                                      className="w-4 h-4 rounded border-slate-300 text-orange-500 focus:ring-orange-500 cursor-pointer"
                                    />
                                  </th>
                                  <th className="w-12 py-3 px-2 text-center text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400" title="Ordre de déchargement">#</th>
                                  <th className="py-3 px-4 w-[180px] text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Description</th>
                                  <th className="py-3 px-4 w-24 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Longueur</th>
                                  <th className="py-3 px-4 w-24 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Largeur</th>
                                  <th className="py-3 px-4 w-24 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Hauteur</th>
                                  <th className="py-3 px-2 w-24 text-right text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400" title="Volume unitaire calculé : longueur × largeur × hauteur">Volume unit.</th>
                                  <th className="py-3 px-4 w-24 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Poids unitaire</th>
                                  <th className="w-28 bg-orange-100 py-3 px-4 text-center text-xs font-semibold uppercase tracking-wider text-orange-800 dark:bg-orange-950/60 dark:text-orange-300">Quantité</th>
                                  <th className="py-3 px-4 w-[220px] text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Contraintes</th>
                                  <th className="py-3 px-2 w-[150px] text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Groupe</th>
                                  <th className="py-3 px-4 w-[136px] text-right text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Actions</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100 dark:divide-slate-800/80">
                                <SortableContext items={formData.items?.map(i => i._dndId || i.id) || []} strategy={verticalListSortingStrategy}>
                                  {groupedItems.map((group) => {
                                    const isCollapsed = collapsedGroups.has(group.key)
                                    const isUngrouped = group.key === '__ungrouped__'
                                    const groupItemCount = group.items.length
                                    const totalQty = group.items.reduce((s, { item }) => s + (item.quantity ?? 1), 0)
                                    const groupVolumeM3 = group.items.reduce((s, { item }) => {
                                      return s + (['length', 'width', 'height'] as const).reduce((v, k) => v * convertMeasurement(item[k], parseMeasurementUnit(item.dimension_unit || 'mm'), 'm'), item.quantity ?? 1)
                                    }, 0)
                                    const groupWeightKg = group.items.reduce((s, { item }) => {
                                      return s + (convertMeasurement(item.weight || 0, parseMeasurementUnit(item.weight_unit || 'kg'), 'kg') * (item.quantity ?? 1))
                                    }, 0)

                                    return (
                                      <Fragment key={`group-block-${group.key}`}>
                                        {/* GROUP HEADER ROW */}
                                        <tr
                                          className={`select-none transition-colors ${isUngrouped
                                            ? 'bg-slate-50/70 dark:bg-slate-900/40'
                                            : 'bg-orange-50/80 dark:bg-orange-950/30'
                                            }`}
                                        >
                                          <td colSpan={14} className="py-0">
                                            <button
                                              type="button"
                                              onClick={() => toggleGroupCollapse(group.key)}
                                              className={`w-full flex items-center justify-between px-3 py-2 text-left transition-colors ${isUngrouped
                                                ? 'hover:bg-slate-100 dark:hover:bg-slate-800/60'
                                                : 'hover:bg-orange-100/70 dark:hover:bg-orange-900/50'
                                                }`}
                                            >
                                              <div className="flex items-center gap-2.5">
                                                <span className={isUngrouped ? 'text-slate-400' : 'text-orange-500'}>
                                                  {isCollapsed ? (
                                                    <ChevronRightIcon className="w-4 h-4" />
                                                  ) : (
                                                    <ChevronDownIcon className="w-4 h-4" />
                                                  )}
                                                </span>

                                                {!isUngrouped && (
                                                  <span
                                                    className="w-3 h-3 rounded-full border border-slate-300 dark:border-slate-600 shadow-xs flex-shrink-0"
                                                    style={{ backgroundColor: getColorFromGroupName(group.groupName) }}
                                                  />
                                                )}

                                                <span className={`text-xs font-extrabold tracking-wide uppercase ${isUngrouped ? 'text-slate-500 dark:text-slate-400 italic' : 'text-orange-700 dark:text-orange-300'
                                                  }`}>
                                                  {isUngrouped ? 'Sans groupe' : `Groupe: ${group.groupName}`}
                                                </span>
                                              </div>

                                              <div className="flex items-center gap-2">
                                                <span className={`text-[11px] font-mono font-semibold px-2.5 py-0.5 rounded-full ${isUngrouped
                                                  ? 'bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300'
                                                  : 'bg-orange-100 text-orange-800 dark:bg-orange-900/60 dark:text-orange-200 border border-orange-200 dark:border-orange-800'
                                                  }`}>
                                                  {groupItemCount} SKU · {totalQty} u. · <Measure value={groupVolumeM3} from="m3" quantity="volume" /> · <Measure value={groupWeightKg} from="kg" quantity="mass" />
                                                </span>
                                              </div>
                                            </button>
                                          </td>
                                        </tr>

                                        {/* ITEM ROWS */}
                                        {!isCollapsed && group.items.map(({ item, globalIndex }) => (
                                          <SortableCargoRow
                                            key={item._dndId || item.id || `fallback-${globalIndex}`}
                                            item={item}
                                            index={globalIndex}
                                            selectedItemIndexes={selectedItemIndexes}
                                            setSelectedItemIndexes={setSelectedItemIndexes}
                                            handleUpdateItemField={handleUpdateItemField}
                                            handleDuplicateItem={handleDuplicateItem}
                                            handleRemoveItem={handleRemoveItem}
                                            handleOpenProductForm={handleOpenProductForm}
                                            availableGroups={availableGroups}
                                          />
                                        ))}
                                      </Fragment>
                                    )
                                  })}
                                </SortableContext>
                              </tbody>
                            </table>
                          </DndContext>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* ─── TAB: ÉQUIPEMENT ─────────────────────────────────── */}
                {activeMainTab === 'equipement' && (
                  <div
                    id="panel-equipement"
                    role="tabpanel"
                    tabIndex={-1}
                    aria-labelledby="maintab-equipement"
                    className="mx-auto w-full max-w-7xl space-y-4 opt-panel-enter"
                  >
                    <section aria-label="Vue immersive de l’équipement" className="optimization-equipment-editor w-full overflow-hidden rounded-2xl border border-slate-200/80 shadow-sm dark:border-slate-800">
                      <VehicleConfigRail
                        context="session"
                        appearance="immersive"
                        showOverview={false}
                        equipmentOverlay={compact => <OptimizationEquipmentOverview
                          floating
                          compact={compact}
                          vehicle={formData.vehicle}
                          loadSpace={formData.loadSpace}
                          tractor={formData.tractor}
                          isSemiTrailer={isSemiTrailer}
                          favorites={VEHICLE_PRESETS}
                          loading={vehiclesLoading}
                          error={vehiclesError}
                          count={vehicles.length}
                          canSelect={equipmentAccess.read && optimizationAccess.write && !equipmentEditsLocked}
                          canWrite={optimizationAccess.write && !equipmentEditsLocked}
                          onSelect={() => setVehicleSearchDialogOpen(true)}
                          onSelectTractor={() => setTractorSearchDialogOpen(true)}
                          onRemoveTractor={() => setFormData(previous => ({ ...previous, tractor: undefined, vehicleCombination: undefined }))}
                          onFavorite={handleSelectVehicle}
                          onRetry={() => void refetchVehicles()}
                        />}
                        mode={canEditEquipment && !equipmentEditsLocked ? 'edit' : 'view'}
                        isTractor={(formData.vehicle?.type as string) === 'TRACTOR' || !!(formData.vehicle as any)?.is_tractor}
                        tractor={formData.tractor || null}
                        coupling={formData.vehicleCombination?.coupling}
                        height="clamp(640px, calc(100dvh - 280px), 960px)"
                        vehicle={{
                          ...formData.vehicle,
                          dimensions: {
                            length: formData.vehicle?.dimensions?.length || formData.loadSpace?.length || 10000,
                            width: formData.vehicle?.dimensions?.width || formData.loadSpace?.width || 2500,
                            height: formData.vehicle?.dimensions?.height || formData.loadSpace?.height || 2500,
                          },
                          capacity_weight: formData.constraints?.maxWeight ?? (formData.vehicle as any)?.capacity_weight,
                          max_floor_pressure: formData.constraints?.maxFloorPressure ?? (formData.vehicle as any)?.max_floor_pressure,
                          pressure_cell_target_size: formData.constraints?.pressureCellTargetSize ?? (formData.vehicle as any)?.pressure_cell_target_size,
                          has_reefer_unit: (formData.vehicle as any)?.has_reefer_unit ?? false,
                          reefer_temperature_range: (formData.vehicle as any)?.reefer_temperature_range ?? null,
                          is_hazardous_compatible: formData.loadSpace?.is_hazardous_compatible ?? (formData.vehicle as any)?.is_hazardous_compatible ?? false,
                          target_cg_x: formData.constraintEngineConfig?.targetCG?.x ?? 0.5,
                          target_cg_y: formData.constraintEngineConfig?.targetCG?.y ?? 0.5,
                          max_cg_deviation: formData.constraintEngineConfig?.thresholds?.maxCenterOfGravityDeviation ?? 0.1,
                          constraintEngineConfig: formData.constraintEngineConfig,
                        } as any}
                        onChange={(updated: any) => {
                          if (!canEditEquipment || equipmentEditsLocked) return
                          const newLen = updated.dimensions?.length ? Math.round(updated.dimensions.length) : undefined
                          const newWidth = updated.dimensions?.width ? Math.round(updated.dimensions.width) : undefined
                          const newHeight = updated.dimensions?.height ? Math.round(updated.dimensions.height) : undefined

                          setFormData(prev => ({
                            ...prev,
                            loadSpace: {
                              ...prev.loadSpace,
                              length: newLen ?? prev.loadSpace?.length ?? 10000,
                              width: newWidth ?? prev.loadSpace?.width ?? 2500,
                              height: newHeight ?? prev.loadSpace?.height ?? 2500,
                              is_hazardous_compatible: updated.is_hazardous_compatible !== undefined ? updated.is_hazardous_compatible : prev.loadSpace?.is_hazardous_compatible,
                              maxWeight: updated.capacity_weight !== undefined ? updated.capacity_weight : prev.loadSpace?.maxWeight,
                              emptyWeight: updated.empty_weight !== undefined ? updated.empty_weight : prev.loadSpace?.emptyWeight,
                              maxTotalWeight: updated.max_weight_permitted !== undefined ? updated.max_weight_permitted : prev.loadSpace?.maxTotalWeight,
                            } as LoadSpace3D,
                            vehicleCombination: synchronizeVehicleCombination(
                              prev.vehicleCombination,
                              {
                                ...prev.vehicle,
                                type: updated.type ?? prev.vehicle?.type,
                                axle_configuration: updated.axle_configuration ?? prev.vehicle?.axle_configuration,
                                fifth_wheel: updated.fifth_wheel ?? (prev.vehicle as any)?.fifth_wheel,
                              } as Vehicle,
                              prev.tractor,
                            ),
                            vehicle: {
                              ...prev.vehicle,
                              type: updated.type ?? prev.vehicle?.type,
                              dimensions: {
                                length: newLen,
                                width: newWidth,
                                height: newHeight,
                              },
                              capacity_weight: updated.capacity_weight !== undefined ? updated.capacity_weight : (prev.vehicle as any)?.capacity_weight,
                              empty_weight: updated.empty_weight !== undefined ? updated.empty_weight : (prev.vehicle as any)?.empty_weight,
                              max_weight_permitted: updated.max_weight_permitted !== undefined ? updated.max_weight_permitted : (prev.vehicle as any)?.max_weight_permitted,
                              max_combination_weight: updated.max_combination_weight !== undefined ? updated.max_combination_weight : (prev.vehicle as any)?.max_combination_weight,
                              mass_contract_version: updated.mass_contract_version !== undefined ? updated.mass_contract_version : (prev.vehicle as any)?.mass_contract_version,
                              mass_provenance: updated.mass_provenance !== undefined ? updated.mass_provenance : (prev.vehicle as any)?.mass_provenance,
                              has_reefer_unit: updated.has_reefer_unit !== undefined ? updated.has_reefer_unit : (prev.vehicle as any)?.has_reefer_unit,
                              reefer_temperature_range: updated.reefer_temperature_range !== undefined ? updated.reefer_temperature_range : (prev.vehicle as any)?.reefer_temperature_range,
                              is_hazardous_compatible: updated.is_hazardous_compatible !== undefined ? updated.is_hazardous_compatible : (prev.vehicle as any)?.is_hazardous_compatible,
                              axle_configuration: updated.axle_configuration ?? prev.vehicle?.axle_configuration,
                              obstacles: updated.obstacles ?? prev.vehicle?.obstacles,
                              fifth_wheel: updated.fifth_wheel ?? (prev.vehicle as any)?.fifth_wheel,
                            },
                            constraints: {
                              ...prev.constraints,
                              maxWeight: updated.capacity_weight !== undefined ? updated.capacity_weight : (updated.constraints?.maxWeight ?? prev.constraints?.maxWeight),
                              maxStackHeight: newHeight ?? updated.constraints?.maxStackHeight ?? prev.constraints?.maxStackHeight,
                              maxFloorPressure: optionalConstraintNumber(updated.max_floor_pressure !== undefined ? updated.max_floor_pressure : (updated.constraints?.maxFloorPressure ?? prev.constraints?.maxFloorPressure)),
                              pressureCellTargetSize: optionalConstraintNumber(updated.pressure_cell_target_size !== undefined ? updated.pressure_cell_target_size : (updated.constraints?.pressureCellTargetSize ?? prev.constraints?.pressureCellTargetSize)),
                            },
                            constraintEngineConfig: {
                              ...prev.constraintEngineConfig,
                              targetCG: {
                                x: updated.target_cg_x ?? prev.constraintEngineConfig?.targetCG?.x ?? 0.5,
                                y: updated.target_cg_y ?? prev.constraintEngineConfig?.targetCG?.y ?? 0.5,
                                z: (updated as any).constraintEngineConfig?.targetCG?.z ?? 0.5
                              },
                              thresholds: {
                                ...(prev.constraintEngineConfig?.thresholds || {}),
                                maxCenterOfGravityDeviation: updated.max_cg_deviation ?? prev.constraintEngineConfig?.thresholds?.maxCenterOfGravityDeviation ?? 0.2
                              }
                            }
                          }))
                        }}
                      />
                    </section>
                  </div>
                )}

                {/* ─── TAB: RÈGLES ─────────────────────────────────────── */}
                {activeMainTab === 'regles' && (
                  <div
                    id="panel-regles"
                    role="tabpanel"
                    tabIndex={-1}
                    aria-labelledby="maintab-regles"
                    className="mx-auto w-full max-w-7xl opt-panel-enter"
                  >
                    <OptimizationRulesPanel
                      input={formData}
                      onChange={setFormData}
                      readOnly={!optimizationAccess.write || equipmentEditsLocked}
                      searchProfile={selectedSearchProfile}
                      searchProfileRef={searchProfileSelectRef}
                      axleMargin={formData.constraints?.axleLoadSafetyMarginPercent ?? globalSettings.thresholds?.axleLoadSafetyMarginPercent ?? 0.05}
                      enforceAxleLimits={resolveVehicleAxleLimitEnforcement(formData.constraints?.enforceAxleLimits, formData.vehicle?.axle_configuration, globalSettings.thresholds?.enforceAxleLimits)}
                      presets={{ ...(settingsData?.defaults?.optimizationPresets || {}), ...(settingsData?.tenantConfig?.optimizationPresets || {}) }}
                      selectedPreset={resolveOptimizationPresetId()}
                      onPresetChange={presetId => {
                        const presets = { ...(settingsData?.defaults?.optimizationPresets || {}), ...(settingsData?.tenantConfig?.optimizationPresets || {}) }
                        setFormData(previous => ({ ...previous, optimizationPresetId: presetId, optimizationWeights: presets[presetId]?.weights ?? settingsData?.defaults?.optimizationWeights }))
                      }}
                      weightsOpen={!collapsedSections.has('constraintWeights')}
                      onWeightsToggle={() => toggleSection('constraintWeights')}
                      measurementControl={<>
                        <MeasurementSystemSelect value={projectSystem} onChange={changeProjectSystem} disabled={!optimizationAccess.write || equipmentEditsLocked || !!projectError || (!!(stableJobId || run?.job_id) && !project) || (!isExistingMode && !settingsData)} />
                        {savingMeasurement && <span role="status" className="text-xs text-slate-500">Enregistrement…</span>}
                        {!!(measurementError || projectError) && <span role="alert" className="text-xs text-red-600 dark:text-red-400">{measurementError || 'Impossible de charger les unités du dossier.'}</span>}
                      </>}
                    />
                  </div>
                )}
              </div>


            </fieldset>
          )
          }

        <section aria-label="Vérification et calcul" className={`${activeMainTab === 'cargaison' ? 'sm:sticky' : ''} bottom-0 z-20 rounded-2xl border border-slate-200/80 bg-white p-3 shadow-[0_-4px_24px_-16px_rgba(15,23,42,0.25)] dark:border-slate-800 dark:bg-slate-900 sm:p-3.5`}>
          <div className="flex flex-wrap items-center justify-end gap-3">
            {(launchUnavailable || (showValidation && preparationIssues.length > 0)) && <p id="launch-reason" className="mr-auto text-xs text-slate-600 dark:text-slate-300">{launchUnavailable || 'Corrigez les données signalées avant de calculer.'}</p>}
            <div className="flex flex-wrap items-center gap-2">
              {activeMainTab !== 'cargaison' && <button type="button" onClick={() => { const tab = activeMainTab === 'regles' ? 'equipement' : 'cargaison'; changePreparationTab(tab) }} className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-semibold dark:border-slate-700">Précédent</button>}
              {activeMainTab !== 'regles' && <button type="button" onClick={() => { const tab = activeMainTab === 'cargaison' ? 'equipement' : 'regles'; changePreparationTab(tab) }} className="rounded-xl border border-orange-200 bg-orange-50 px-3 py-2.5 text-sm font-semibold text-orange-800 dark:border-orange-900 dark:bg-orange-950/30 dark:text-orange-200">{activeMainTab === 'cargaison' ? 'Suivant : équipement' : 'Suivant : règles'}</button>}
              {searchModeReminder}
              <button type="button" onClick={handleSubmit} disabled={!canLaunch} aria-describedby={launchUnavailable || (showValidation && preparationIssues.length > 0) ? 'launch-reason' : undefined} aria-busy={createMutation.isPending} data-e2e="btn-run-optimization" className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-orange-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-orange-800 disabled:cursor-not-allowed disabled:opacity-50"><RocketLaunchIcon className="h-4 w-4" />{createMutation.isPending ? 'Lancement…' : preparationIssues.length ? 'Vérifier les données' : isExistingMode ? 'Recalculer une nouvelle version' : 'Calculer le chargement'}</button>
            </div>
          </div>
        </section>
        </main >
        {editingProjectInfo && project && <CreateProjectDialog project={project} onClose={() => setEditingProjectInfo(false)} onSaved={() => void refetchProject()} />}

        {/* ── ProductForm Modal for advanced settings ──────────────────────── */}
        {
          showProductFormModal && itemAsProduct && (
            <ProductForm
              product={itemAsProduct}
              onSave={handleSaveProductForm}
              onClose={() => {
                setShowProductFormModal(false)
                setEditingItemIndex(null)
              }}
              isLoading={false}
              adrClasses={adrClasses}
              unNumbers={unNumbers}
            />
          )
        }

        {/* Group Assignment Dialog */}
        <Dialog
          isOpen={groupDialogOpen}
          onClose={() => setGroupDialogOpen(false)}
          title="Affecter un groupe"
          size="sm"
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Affectez un groupe aux {selectedItemIndexes.length} article{selectedItemIndexes.length > 1 ? 's' : ''} sélectionné{selectedItemIndexes.length > 1 ? 's' : ''}. Les articles du même groupe seront chargés ensemble.
            </p>

            <div>
              <label htmlFor="group-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Nom du groupe
              </label>
              <input
                id="group-name"
                type="text"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                placeholder="ex: C-1458"
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    if (newGroupName.trim()) {
                      setFormData(prev => {
                        const newItems = [...(prev.items || [])];
                        selectedItemIndexes.forEach(idx => {
                          if (newItems[idx]) {
                            newItems[idx] = { ...newItems[idx], keep_together_group: newGroupName.trim() };
                          }
                        });
                        return { ...prev, items: newItems };
                      });
                      setGroupDialogOpen(false);
                      setNewGroupName('');
                    }
                  }
                }}
              />
            </div>

            {availableGroups.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Groupes existants
                </label>
                <div className="flex flex-wrap gap-2">
                  {availableGroups.map(group => (
                    <button
                      key={group}
                      type="button"
                      onClick={() => setNewGroupName(group)}
                      className="px-3 py-1.5 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors"
                    >
                      {group}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setGroupDialogOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={() => {
                  if (newGroupName.trim()) {
                    setFormData(prev => {
                      const newItems = [...(prev.items || [])];
                      selectedItemIndexes.forEach(idx => {
                        if (newItems[idx]) {
                          newItems[idx] = { ...newItems[idx], keep_together_group: newGroupName.trim() };
                        }
                      });
                      return { ...prev, items: newItems };
                    });
                    setGroupDialogOpen(false);
                    setNewGroupName('');
                  }
                }}
                disabled={!newGroupName.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed rounded-lg transition-colors"
              >
                Affecter
              </button>
            </div>
          </div>
        </Dialog>

        {/* Manage Groups Dialog */}
        <Dialog
          isOpen={manageGroupsDialogOpen}
          onClose={() => {
            setManageGroupsDialogOpen(false)
            setEditingGroupName(null)
            setEditedGroupName('')
          }}
          title="Gérer les groupes"
          size="md"
        >
          <div className="space-y-4">
            {availableGroups.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                Aucun groupe existant
              </p>
            ) : (
              <div className="space-y-2">
                {availableGroups.map(group => {
                  const groupItems = formData.items?.filter(item => item.keep_together_group === group) || []
                  const groupColor = groupItems.length > 0 ? groupItems[0].color : getColorFromGroupName(group)

                  return (
                    <div
                      key={group}
                      className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600"
                    >
                      {editingGroupName === group ? (
                        <>
                          <input
                            type="text"
                            value={editedGroupName}
                            onChange={(e) => setEditedGroupName(e.target.value)}
                            className="flex-1 px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault()
                                if (editedGroupName.trim() && editedGroupName.trim() !== group) {
                                  setFormData(prev => {
                                    const newItems = [...(prev.items || [])];
                                    newItems.forEach(item => {
                                      if (item.keep_together_group === group) {
                                        item.keep_together_group = editedGroupName.trim();
                                      }
                                    });
                                    return { ...prev, items: newItems };
                                  });
                                  setEditingGroupName(null);
                                  setEditedGroupName('');
                                }
                              } else if (e.key === 'Escape') {
                                setEditingGroupName(null);
                                setEditedGroupName('');
                              }
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              if (editedGroupName.trim() && editedGroupName.trim() !== group) {
                                setFormData(prev => {
                                  const newItems = [...(prev.items || [])];
                                  newItems.forEach(item => {
                                    if (item.keep_together_group === group) {
                                      item.keep_together_group = editedGroupName.trim();
                                    }
                                  });
                                  return { ...prev, items: newItems };
                                });
                              }
                              setEditingGroupName(null);
                              setEditedGroupName('');
                            }}
                            className="px-3 py-1.5 text-xs font-medium text-green-600 hover:text-green-700 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/25 border border-green-200 dark:border-green-800/60 rounded-lg transition-colors"
                          >
                            Valider
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setEditingGroupName(null);
                              setEditedGroupName('');
                            }}
                            className="px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg transition-colors"
                          >
                            Annuler
                          </button>
                        </>
                      ) : (
                        <>
                          <div
                            className="w-8 h-8 rounded-md border-2 border-gray-300 dark:border-gray-600 cursor-pointer hover:scale-110 transition-transform"
                            style={{ backgroundColor: groupColor }}
                            title={`Couleur du groupe : ${groupColor}`}
                          >
                            <input
                              type="color"
                              value={groupColor}
                              onChange={(e) => {
                                const newColor = e.target.value
                                setFormData(prev => {
                                  const newItems = [...(prev.items || [])];
                                  newItems.forEach(item => {
                                    if (item.keep_together_group === group) {
                                      item.color = newColor;
                                    }
                                  });
                                  return { ...prev, items: newItems };
                                });
                              }}
                              className="w-full h-full opacity-0 cursor-pointer"
                              title="Modifier la couleur du groupe"
                            />
                          </div>
                          <div className="flex-1">
                            <span className="text-sm font-medium text-gray-900 dark:text-white">{group}</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                              ({groupItems.length} article{groupItems.length > 1 ? 's' : ''})
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setEditingGroupName(group);
                              setEditedGroupName(group);
                            }}
                            className="px-3 py-1.5 text-xs font-medium text-purple-600 hover:text-purple-700 hover:bg-purple-50 dark:text-purple-400 dark:hover:bg-purple-900/25 border border-purple-200 dark:border-purple-800/60 rounded-lg transition-colors"
                          >
                            Renommer
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              if (window.confirm(`Supprimer le groupe "${group}" ? Les articles ne seront plus groupés.`)) {
                                setFormData(prev => {
                                  const newItems = [...(prev.items || [])];
                                  newItems.forEach(item => {
                                    if (item.keep_together_group === group) {
                                      item.keep_together_group = '';
                                    }
                                  });
                                  return { ...prev, items: newItems };
                                });
                              }
                            }}
                            className="px-3 py-1.5 text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/25 border border-red-200 dark:border-red-800/60 rounded-lg transition-colors"
                          >
                            Supprimer
                          </button>
                        </>
                      )}
                    </div>
                  )
                })}
              </div>
            )}

            <div className="flex items-center justify-end pt-2">
              <button
                type="button"
                onClick={() => {
                  setManageGroupsDialogOpen(false)
                  setEditingGroupName(null)
                  setEditedGroupName('')
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </Dialog>

        {catalogDialogOpen && optimizationAccess.write && access.resource('product').read && !runIsActive && !createMutation.isPending && (
          <ProductCatalogDialog key={user?.tenantId ?? user?.id} currentItems={formData.items || []} onClose={() => setCatalogDialogOpen(false)} onAddProducts={handleAddCatalogProducts} />
        )}

        {/* ── CSV Import / Export Modal ─────────────────────────────────────── */}
        <CargoCsvDialog
          isOpen={csvDialogOpen}
          onClose={() => setCsvDialogOpen(false)}
          onImportItems={handleImportCsvItems}
          currentItems={formData.items || []}
        />

        {/* ── Vehicle Search Dialog ─────────────────────────────────────────── */}
        <VehicleSearchDialog
          isOpen={vehicleSearchDialogOpen}
          onClose={() => setVehicleSearchDialogOpen(false)}
          vehicles={vehicles}
          isLoading={vehiclesLoading}
          error={vehiclesError}
          onRetry={() => void refetchVehicles()}
          currentVehicleId={selectedVehicleDetails?.id}
          currentVehicleReg={selectedVehicleDetails?.registration_number || formData.vehicle?.registration_number}
          onSelectVehicle={handleSelectVehicle}
        />

        {/* ── Tractor Search Dialog (for semi-trailers) ─────────────────────────── */}
        <VehicleSearchDialog
          isOpen={tractorSearchDialogOpen}
          onClose={() => setTractorSearchDialogOpen(false)}
          title="Sélectionner un tracteur pour la semi-remorque"
          vehicles={vehicles.filter(isTractorCandidate)}
          isLoading={vehiclesLoading}
          error={vehiclesError}
          onRetry={() => void refetchVehicles()}
          currentVehicleId={formData.tractor?.id}
          currentVehicleReg={formData.tractor?.registration_number}
          onSelectVehicle={handleSelectTractor}
        />

        {/* ── Animations ─────────────────────────────────────────────────────── */}
        <style>{`
        @keyframes opt-progress {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(350%); }
        }
        .opt-progress-bar {
          animation: opt-progress 1.4s ease-in-out infinite;
          width: 30%;
        }
        @keyframes opt-fade-in {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .opt-panel-enter {
          animation: opt-fade-in 0.18s ease-out both;
        }
        @keyframes slide-in-right {
          from { transform: translateX(100%); }
          to   { transform: translateX(0); }
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.22s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        .animate-spin-slow {
          animation: spin-slow 8s linear infinite;
        }
      `}</style>
      </div >
    </MeasurementProvider>
  )
}
